/*
 * RebX trade-in form enhancements
 * - Drag & drop photo box with client-side previews and validation
 * - "Valuation speed bar" that fills as photos are added
 * - Optional upload to an unsigned endpoint (e.g. Cloudinary) so hosted
 *   photo links are included in the contact email. Shopify's contact form
 *   can't email file attachments, so without an endpoint the box stays a
 *   guided preview and records the file names only.
 */
(function () {
  'use strict';

  var ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif'];

  var GRADE_ASSETS = {
    condExcellent: 'rebx-tradein-condition-excellent.svg',
    condVeryGood: 'rebx-tradein-condition-very-good.svg',
    condGood: 'rebx-tradein-condition-good.svg',
    condFair: 'rebx-tradein-condition-fair.svg',
    condFaulty: 'rebx-tradein-condition-faulty.svg',
    screenFlawless: 'rebx-tradein-screen-flawless.svg',
    screenMinor: 'rebx-tradein-screen-minor.svg',
    screenVisible: 'rebx-tradein-screen-visible.svg',
    screenCracked: 'rebx-tradein-screen-cracked.svg',
    bodyFlawless: 'rebx-tradein-body-flawless.svg',
    bodyLight: 'rebx-tradein-body-light.svg',
    bodyHeavy: 'rebx-tradein-body-heavy.svg',
    bodyDamaged: 'rebx-tradein-body-damaged.svg'
  };

  function gradeOpt(title, desc, assetKey) {
    return title + ' — ' + desc + ' | asset:' + GRADE_ASSETS[assetKey];
  }

  function tradeTokens(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\+/g, ' plus ')
      .replace(/(\d+(?:\.\d+)?)\s*(gb|tb)\b/g, '$1$2')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  }

  function loadTradePrices(url) {
    if (window.RebxTradeInPriceRows) return Promise.resolve(window.RebxTradeInPriceRows);
    if (window.RebxTradeInPriceRowsPromise) return window.RebxTradeInPriceRowsPromise;
    if (!url) return Promise.resolve([]);
    window.RebxTradeInPriceRowsPromise = fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('prices');
        return res.json();
      })
      .then(function (rows) {
        var prepared = (rows || []).map(function (row) {
          return {
            name: row.name,
            cash: Math.round(Number(row.cash)),
            tokens: tradeTokens(row.name)
          };
        }).filter(function (row) {
          return row.tokens.length && row.cash > 0;
        });
        prepared.sort(function (a, b) {
          return b.tokens.length - a.tokens.length || b.name.length - a.name.length;
        });
        window.RebxTradeInPriceRows = prepared;
        return prepared;
      })
      .catch(function () {
        window.RebxTradeInPriceRows = [];
        return [];
      });
    return window.RebxTradeInPriceRowsPromise;
  }

  function matchTradePrice(title) {
    var rows = window.RebxTradeInPriceRows;
    if (!rows || !rows.length || !title) return null;
    var have = {};
    tradeTokens(title).forEach(function (token) { have[token] = true; });
    for (var i = 0; i < rows.length; i++) {
      var tokens = rows[i].tokens;
      var ok = true;
      for (var j = 0; j < tokens.length; j++) {
        if (!have[tokens[j]]) {
          ok = false;
          break;
        }
      }
      if (ok) return rows[i];
    }
    return null;
  }

  var WORKING_CONDITION = [
    gradeOpt('Excellent', 'Like new. No scratches or marks, fully working', 'condExcellent'),
    gradeOpt('Very Good', 'Minor signs of use, barely noticeable, fully working', 'condVeryGood'),
    gradeOpt('Good', 'Light signs of use, no cracks, fully working', 'condGood'),
    gradeOpt('Fair', 'Visible scratches or wear, still fully working', 'condFair')
  ];

  var CONSOLE_CONDITION = WORKING_CONDITION.concat([
    gradeOpt('Faulty', "Won't boot or has a fault — we can repair it", 'condFaulty')
  ]);

  var WORKING_SCREEN = [
    gradeOpt('Flawless', "No marks at all, even when off", 'screenFlawless'),
    gradeOpt('Minor marks', "Tiny scratches you can't see when it's on", 'screenMinor'),
    gradeOpt('Visible scratches', "Scratches you can see when it's on", 'screenVisible'),
    gradeOpt('Cracked glass', 'Chipped or cracked, screen still works', 'screenCracked')
  ];

  var WORKING_BODY = [
    gradeOpt('Flawless', 'No marks on the frame, back or camera glass', 'bodyFlawless'),
    gradeOpt('Light wear', 'Small scuffs or marks, nothing major', 'bodyLight'),
    gradeOpt('Heavy wear', 'Dents or deep scratches', 'bodyHeavy'),
    gradeOpt('Damaged', 'Bent, or cracked back / camera glass', 'bodyDamaged')
  ];

  var CONSOLE_BODY = [
    gradeOpt('Flawless', 'No marks on the shell or ports', 'bodyFlawless'),
    gradeOpt('Light wear', 'Small scuffs or marks, nothing major', 'bodyLight'),
    gradeOpt('Heavy wear', 'Dents or deep scratches', 'bodyHeavy'),
    gradeOpt('Damaged', 'Cracked shell or missing parts — we may still repair it', 'bodyDamaged')
  ];

  var WORKING_GRADE_HINTS = {
    condition: 'Pick the closest match — your device must be fully working. We double-check on arrival.',
    screen: "Tip: check the screen turned on, in good light, at an angle. It must display and respond normally.",
    body: 'Check the frame, the back and the camera lens glass.'
  };

  var CONSOLE_GRADE_HINTS = {
    condition: 'Consoles can be faulty — we refurbish them in-house. Pick the closest match.',
    body: 'Check the shell, ports, disc drive and any controller wear.'
  };

  var NON_WORKING_RE = /faulty|won't boot|won't display|not working|for spares|dead pixel/i;

  /*
   * Device profiles — the brains behind device-aware questions.
   * `fields` lists which question keys are shown for that device type.
   * Keys: model, storage, connectivity, caseSize, ram, processor, imei,
   *       condition, screen, body, network, battery, accessories.
   * Option arrays (storage/connectivity/ram/caseSize/accessories) replace the
   * cards for that group. imeiLabel/imeiHelp relabel the identifier field, and
   * imeiRequired:false makes it optional. Merchants can override via the
   * "Device rules JSON" setting.
   */
  var DEFAULT_PROFILES = {
    phone: {
      fields: ['model', 'storage', 'imei', 'condition', 'screen', 'body', 'network', 'battery', 'accessories'],
      condition: WORKING_CONDITION,
      screen: WORKING_SCREEN,
      body: WORKING_BODY,
      gradeHints: WORKING_GRADE_HINTS,
      storage: ['Not sure', '128GB', '256GB', '512GB', '1TB'],
      imeiLabel: 'IMEI number',
      imeiHelp: 'Dial *#06# or check Settings to find it. We use it to confirm the phone isn’t lost or blocked.',
      accessories: ['Original box', 'Charger / cable', 'Receipt / proof of purchase', 'Case or accessories'],
      modelPlaceholder: 'e.g. iPhone 17 Pro',
      specs: {
        'iPhone 17 Pro Max': { storage: ['256GB', '512GB', '1TB', '2TB'] },
        'iPhone 17 Pro': { storage: ['256GB', '512GB', '1TB'] },
        'iPhone Air': { storage: ['256GB', '512GB', '1TB'] },
        'iPhone 17': { storage: ['256GB', '512GB'] },
        'iPhone 17e': { storage: ['128GB', '256GB'] },
        'iPhone 16 Pro Max': { storage: ['256GB', '512GB', '1TB'] },
        'iPhone 16 Pro': { storage: ['128GB', '256GB', '512GB', '1TB'] },
        'iPhone 16 Plus': { storage: ['128GB', '256GB', '512GB'] },
        'iPhone 16': { storage: ['128GB', '256GB', '512GB'] },
        'iPhone 16e': { storage: ['128GB', '256GB', '512GB'] },
        'iPhone 15 Pro Max': { storage: ['256GB', '512GB', '1TB'] },
        'iPhone 15 Pro': { storage: ['128GB', '256GB', '512GB', '1TB'] },
        'iPhone 15 Plus': { storage: ['128GB', '256GB', '512GB'] },
        'iPhone 15': { storage: ['128GB', '256GB', '512GB'] },
        'iPhone 14 Pro Max': { storage: ['128GB', '256GB', '512GB', '1TB'] },
        'iPhone 14 Pro': { storage: ['128GB', '256GB', '512GB', '1TB'] },
        'iPhone 14 Plus': { storage: ['128GB', '256GB', '512GB'] },
        'iPhone 14': { storage: ['128GB', '256GB', '512GB'] },
        'iPhone SE (2022)': { storage: ['64GB', '128GB', '256GB'] },
        'Galaxy S26 Ultra': { storage: ['256GB', '512GB', '1TB'] },
        'Galaxy S26+': { storage: ['256GB', '512GB'] },
        'Galaxy S26': { storage: ['128GB', '256GB', '512GB'] },
        'Galaxy S25 Ultra': { storage: ['256GB', '512GB', '1TB'] },
        'Galaxy S25+': { storage: ['256GB', '512GB'] },
        'Galaxy S25': { storage: ['128GB', '256GB', '512GB'] },
        'Galaxy Z Fold7': { storage: ['256GB', '512GB', '1TB'] },
        'Galaxy Z Flip7': { storage: ['256GB', '512GB'] },
        'Galaxy S24 Ultra': { storage: ['256GB', '512GB', '1TB'] },
        'Galaxy S24+': { storage: ['256GB', '512GB'] },
        'Galaxy S24': { storage: ['128GB', '256GB', '512GB'] },
        'Galaxy Z Fold6': { storage: ['256GB', '512GB', '1TB'] },
        'Galaxy Z Flip6': { storage: ['256GB', '512GB'] },
        'Galaxy S23 Ultra': { storage: ['256GB', '512GB', '1TB'] },
        'Galaxy S23+': { storage: ['256GB', '512GB'] },
        'Galaxy S23': { storage: ['128GB', '256GB'] },
        'Galaxy S22 Ultra': { storage: ['128GB', '256GB', '512GB', '1TB'] },
        'Galaxy S22+': { storage: ['128GB', '256GB'] },
        'Galaxy S22': { storage: ['128GB', '256GB'] },
        'Galaxy A56': { storage: ['128GB', '256GB'] },
        'Galaxy A55': { storage: ['128GB', '256GB'] },
        'Pixel 10 Pro XL': { storage: ['256GB', '512GB', '1TB'] },
        'Pixel 10 Pro': { storage: ['128GB', '256GB', '512GB', '1TB'] },
        'Pixel 10': { storage: ['128GB', '256GB'] },
        'Pixel 10a': { storage: ['128GB'] },
        'Pixel 9 Pro XL': { storage: ['256GB', '512GB', '1TB'] },
        'Pixel 9 Pro': { storage: ['128GB', '256GB', '512GB', '1TB'] },
        'Pixel 9': { storage: ['128GB', '256GB'] },
        'Pixel 9a': { storage: ['128GB', '256GB'] },
        'Pixel 8 Pro': { storage: ['128GB', '256GB', '512GB', '1TB'] },
        'Pixel 8': { storage: ['128GB', '256GB'] },
        'Pixel 8a': { storage: ['128GB', '256GB'] },
        'Pixel 7 Pro': { storage: ['128GB', '256GB', '512GB'] },
        'Pixel 7': { storage: ['128GB', '256GB'] },
        'Pixel 7a': { storage: ['128GB'] }
      },
      makes: {
        Apple: ['iPhone 17 Pro Max', 'iPhone 17 Pro', 'iPhone Air', 'iPhone 17', 'iPhone 17e', 'iPhone 16 Pro Max', 'iPhone 16 Pro', 'iPhone 16 Plus', 'iPhone 16', 'iPhone 16e', 'iPhone 15 Pro Max', 'iPhone 15 Pro', 'iPhone 15 Plus', 'iPhone 15', 'iPhone 14 Pro Max', 'iPhone 14 Pro', 'iPhone 14 Plus', 'iPhone 14', 'iPhone SE (2022)'],
        Samsung: ['Galaxy S26 Ultra', 'Galaxy S26+', 'Galaxy S26', 'Galaxy S25 Ultra', 'Galaxy S25+', 'Galaxy S25', 'Galaxy Z Fold7', 'Galaxy Z Flip7', 'Galaxy S24 Ultra', 'Galaxy S24+', 'Galaxy S24', 'Galaxy Z Fold6', 'Galaxy Z Flip6', 'Galaxy S23 Ultra', 'Galaxy S23+', 'Galaxy S23', 'Galaxy S22 Ultra', 'Galaxy S22+', 'Galaxy S22', 'Galaxy A56', 'Galaxy A55'],
        Google: ['Pixel 10 Pro XL', 'Pixel 10 Pro', 'Pixel 10', 'Pixel 10a', 'Pixel 9 Pro XL', 'Pixel 9 Pro', 'Pixel 9', 'Pixel 9a', 'Pixel 8 Pro', 'Pixel 8', 'Pixel 8a', 'Pixel 7 Pro', 'Pixel 7', 'Pixel 7a'],
        OnePlus: ['OnePlus 13', 'OnePlus 12', 'OnePlus 11', 'OnePlus Nord 4', 'OnePlus Nord 3'],
        Xiaomi: ['Xiaomi 15', 'Xiaomi 14', 'Redmi Note 14 Pro', 'Redmi Note 13 Pro'],
        Motorola: ['Motorola Edge 60', 'Razr 60', 'Razr 50', 'Moto G84']
      }
    },
    laptop: {
      fields: ['model', 'storage', 'ram', 'processor', 'imei', 'condition', 'screen', 'body', 'battery', 'accessories'],
      condition: WORKING_CONDITION,
      screen: WORKING_SCREEN,
      body: WORKING_BODY,
      gradeHints: WORKING_GRADE_HINTS,
      storage: ['Not sure', '128GB', '256GB', '512GB', '1TB', '2TB', '4TB or more'],
      ram: ['Not sure', '8GB', '16GB', '18GB', '24GB', '32GB', '64GB or more'],
      imeiLabel: 'Serial number',
      imeiHelp: 'On a Mac: Apple menu → About This Mac. On Windows: check the underside or system info.',
      accessories: ['Original box', 'Charger / power adapter', 'Receipt / proof of purchase'],
      modelPlaceholder: 'e.g. MacBook Air 13" (M4)',
      makes: {
        Apple: ['MacBook Pro 16"', 'MacBook Pro 14"', 'MacBook Air 15"', 'MacBook Air 13"'],
        Dell: ['XPS 13', 'XPS 14', 'XPS 16', 'Inspiron', 'Latitude', 'Alienware'],
        HP: ['Spectre', 'Envy', 'Pavilion', 'EliteBook', 'Omen', 'Victus'],
        Lenovo: ['ThinkPad', 'Yoga', 'IdeaPad', 'Legion', 'LOQ'],
        Asus: ['Zenbook', 'Vivobook', 'ROG', 'TUF Gaming'],
        Acer: ['Swift', 'Aspire', 'Predator', 'Nitro'],
        Microsoft: ['Surface Laptop', 'Surface Laptop Studio']
      }
    },
    watch: {
      fields: ['model', 'caseSize', 'connectivity', 'imei', 'condition', 'screen', 'body', 'battery', 'accessories'],
      condition: WORKING_CONDITION,
      screen: WORKING_SCREEN,
      body: WORKING_BODY,
      gradeHints: WORKING_GRADE_HINTS,
      connectivity: ['GPS only', 'GPS + Cellular'],
      caseSize: ['Not sure', '40mm or smaller', '41–43mm', '44–46mm', '47mm or larger'],
      imeiLabel: 'Serial number',
      imeiHelp: 'Check the back of the watch, the case, or the paired phone’s Watch app.',
      accessories: ['Original box', 'Charger / cable', 'Extra straps / bands', 'Receipt / proof of purchase'],
      modelPlaceholder: 'e.g. Apple Watch Series 11',
      specs: {
        'Apple Watch Series 11': { caseSize: ['42mm', '46mm'] },
        'Apple Watch Ultra 3': { caseSize: ['49mm'] },
        'Apple Watch SE 3': { caseSize: ['40mm', '44mm'] },
        'Apple Watch Series 10': { caseSize: ['42mm', '46mm'] },
        'Apple Watch Ultra 2': { caseSize: ['49mm'] },
        'Apple Watch Series 9': { caseSize: ['41mm', '45mm'] },
        'Apple Watch SE (2nd gen)': { caseSize: ['40mm', '44mm'] },
        'Apple Watch Series 8': { caseSize: ['41mm', '45mm'] },
        'Apple Watch Ultra': { caseSize: ['49mm'] },
        'Galaxy Watch8': { caseSize: ['40mm', '44mm'] },
        'Galaxy Watch8 Classic': { caseSize: ['46mm'] },
        'Galaxy Watch Ultra': { caseSize: ['47mm'] },
        'Galaxy Watch7': { caseSize: ['40mm', '44mm'] },
        'Galaxy Watch6 Classic': { caseSize: ['43mm', '47mm'] },
        'Galaxy Watch6': { caseSize: ['40mm', '44mm'] },
        'Galaxy Watch5 Pro': { caseSize: ['45mm'] },
        'Galaxy Watch5': { caseSize: ['40mm', '44mm'] },
        'Pixel Watch 4': { caseSize: ['41mm', '45mm'] },
        'Pixel Watch 3': { caseSize: ['41mm', '45mm'] },
        'Pixel Watch 2': { caseSize: ['41mm'] }
      },
      makes: {
        Apple: ['Apple Watch Series 11', 'Apple Watch Ultra 3', 'Apple Watch SE 3', 'Apple Watch Series 10', 'Apple Watch Ultra 2', 'Apple Watch Series 9', 'Apple Watch SE (2nd gen)', 'Apple Watch Series 8', 'Apple Watch Ultra'],
        Samsung: ['Galaxy Watch8', 'Galaxy Watch8 Classic', 'Galaxy Watch Ultra', 'Galaxy Watch7', 'Galaxy Watch6 Classic', 'Galaxy Watch6', 'Galaxy Watch5 Pro', 'Galaxy Watch5'],
        Google: ['Pixel Watch 4', 'Pixel Watch 3', 'Pixel Watch 2'],
        Garmin: ['Fenix', 'Epix', 'Forerunner', 'Venu', 'Instinct']
      }
    },
    console: {
      fields: ['model', 'storage', 'imei', 'condition', 'body', 'accessories'],
      condition: CONSOLE_CONDITION,
      body: CONSOLE_BODY,
      gradeHints: CONSOLE_GRADE_HINTS,
      storage: ['Not sure', '32GB', '64GB', '256GB', '512GB', '825GB', '1TB', '2TB'],
      imeiLabel: 'Serial number',
      imeiHelp: 'Printed on the underside or back of the console.',
      accessories: ['Controller(s)', 'Power cable', 'HDMI cable', 'Original box', 'Games / discs', 'Receipt / proof of purchase'],
      modelPlaceholder: 'e.g. PlayStation 5 Pro',
      specs: {
        'PlayStation 5 Pro': { storage: ['2TB'] },
        'PlayStation 5 Slim': { storage: ['1TB'] },
        'PlayStation 5': { storage: ['825GB'] },
        'PlayStation 5 Digital Edition': { storage: ['825GB', '1TB'] },
        'Xbox Series X (2TB)': { storage: ['2TB'] },
        'Xbox Series X': { storage: ['1TB'] },
        'Xbox Series S': { storage: ['512GB', '1TB'] },
        'Switch 2': { storage: ['256GB'] },
        'Switch OLED': { storage: ['64GB'] },
        'Switch': { storage: ['32GB'] },
        'Switch Lite': { storage: ['32GB'] }
      },
      makes: {
        Sony: ['PlayStation 5 Pro', 'PlayStation 5 Slim', 'PlayStation 5', 'PlayStation 5 Digital Edition'],
        Microsoft: ['Xbox Series X (2TB)', 'Xbox Series X', 'Xbox Series S'],
        Nintendo: ['Switch 2', 'Switch OLED', 'Switch', 'Switch Lite']
      }
    },
    audio: {
      fields: ['model', 'imei', 'condition', 'body', 'accessories'],
      condition: WORKING_CONDITION,
      body: WORKING_BODY,
      gradeHints: {
        condition: WORKING_GRADE_HINTS.condition,
        body: WORKING_GRADE_HINTS.body
      },
      imeiLabel: 'Serial number (if available)',
      imeiHelp: 'Often printed inside the charging case or on the device.',
      imeiRequired: false,
      accessories: ['Charging case', 'Charger / cable', 'Ear tips / pads', 'Original box', 'Receipt / proof of purchase'],
      modelPlaceholder: 'e.g. AirPods Pro 3',
      makes: {
        Apple: ['AirPods Pro 3', 'AirPods 4 (ANC)', 'AirPods 4', 'AirPods Pro 2', 'AirPods Max (USB-C)'],
        Sony: ['WH-1000XM6', 'WH-1000XM5', 'WF-1000XM5', 'ULT Wear'],
        Bose: ['QuietComfort Ultra Headphones', 'QuietComfort Ultra Earbuds', 'QuietComfort Headphones', 'QuietComfort Earbuds II'],
        Samsung: ['Galaxy Buds3 Pro', 'Galaxy Buds3', 'Galaxy Buds FE', 'Galaxy Buds2 Pro'],
        Beats: ['Powerbeats Pro 2', 'Solo 4', 'Studio Pro', 'Studio Buds +'],
        JBL: ['Tour Pro 3', 'Live Beam 3', 'Charge 6', 'Tune']
      }
    },
    other: {
      fields: ['model', 'imei', 'condition', 'body', 'accessories'],
      condition: WORKING_CONDITION,
      body: WORKING_BODY,
      gradeHints: {
        condition: WORKING_GRADE_HINTS.condition,
        body: WORKING_GRADE_HINTS.body
      },
      imeiLabel: 'Serial number (if available)',
      imeiRequired: false,
      accessories: ['Original box', 'Charger / cable', 'Receipt / proof of purchase'],
      modelPlaceholder: 'e.g. brand and model'
    }
  };

  function bytesToMb(bytes) {
    return bytes / (1024 * 1024);
  }

  function TradeInForm(root) {
    this.root = root;
    this.maxPhotos = parseInt(root.getAttribute('data-max-photos'), 10) || 6;
    this.maxMb = parseFloat(root.getAttribute('data-max-photo-mb')) || 10;
    this.speedBoost = parseInt(root.getAttribute('data-speed-boost'), 10) || 70;
    this.photosRequired = root.getAttribute('data-photos-required') === 'true';
    this.wizardEnabled = root.getAttribute('data-wizard-enabled') !== 'false';

    this.endpoint = (root.getAttribute('data-upload-endpoint') || '').trim();
    this.uploadField = root.getAttribute('data-upload-field') || 'file';
    this.uploadPreset = (root.getAttribute('data-upload-preset') || '').trim();
    this.uploadPresetField = root.getAttribute('data-upload-preset-field') || 'upload_preset';

    this.form = root.querySelector('form');
    this.drop = root.querySelector('[data-tradein-drop]');
    this.input = root.querySelector('[data-tradein-input]');
    this.thumbs = root.querySelector('[data-tradein-thumbs]');
    this.errorEl = root.querySelector('[data-tradein-photo-error]');
    this.summaryField = root.querySelector('[data-tradein-summary]');
    this.linksField = root.querySelector('[data-tradein-links]');

    this.speedFill = root.querySelector('[data-speed-fill]');
    this.speedValue = root.querySelector('[data-speed-value]');

    this.submitBtn = root.querySelector('[data-tradein-submit]');
    this.submitLabel = root.querySelector('[data-submit-label]');
    this.statusEl = root.querySelector('[data-tradein-status]');

    // Wizard elements
    this.wizard = root.querySelector('[data-wizard]');
    this.steps = this.wizard ? Array.prototype.slice.call(this.wizard.querySelectorAll('[data-step]')) : [];
    this.progress = root.querySelector('[data-wizard-progress]');
    this.bar = root.querySelector('[data-wizard-bar]');
    this.stepCurrentEl = root.querySelector('[data-step-current]');
    this.stepTotalEl = root.querySelector('[data-step-total]');
    this.stepNameEl = root.querySelector('[data-step-name]');
    this.backBtn = root.querySelector('[data-wizard-back]');
    this.nextBtn = root.querySelector('[data-wizard-next]');
    this.reviewBox = root.querySelector('[data-review]');
    this.reviewList = root.querySelector('[data-review-list]');
    this.current = 0;

    // Conditional payment fields
    this.payRadios = Array.prototype.slice.call(
      root.querySelectorAll('input[name="contact[Payment method]"]')
    );
    this.payGroups = Array.prototype.slice.call(root.querySelectorAll('[data-pay-group]'));

    // Device-aware questions
    this.categoryRadios = Array.prototype.slice.call(
      root.querySelectorAll('input[name="contact[Device category]"]')
    );
    this.deviceFields = Array.prototype.slice.call(root.querySelectorAll('[data-device-field]'));
    this.imeiLabelEl = root.querySelector('[data-device-imei-label]');
    this.imeiHintEl = root.querySelector('[data-device-imei-hint]');
    this.imeiInput = root.querySelector('input[name="contact[IMEI or serial]"]');
    this.imeiLabelDefault = this.imeiLabelEl ? this.imeiLabelEl.textContent : '';
    this.imeiHintDefault = this.imeiHintEl ? this.imeiHintEl.textContent : '';
    this.accessoriesGrid = root.querySelector('[data-accessories-grid]');
    this.processorInput = root.querySelector('input[name="contact[Processor]"]');
    this.profiles = this.loadProfiles();
    this.forceActive = null;
    this.modelMode = 'choice';

    // End-to-end encryption of sensitive fields
    this.encryptOn = root.getAttribute('data-encrypt') === 'true';
    this.publicKeyB64 = (root.getAttribute('data-public-key') || '').trim();
    this.secureFields = Array.prototype.slice.call(root.querySelectorAll('[data-secure]'));
    this.securePayloadField = root.querySelector('[data-secure-payload]');
    this.cryptoOk = !!(window.crypto && window.crypto.subtle);
    this.secureEnabled =
      this.encryptOn &&
      this.publicKeyB64 &&
      this.secureFields.length > 0 &&
      !!this.securePayloadField &&
      this.cryptoOk;
    this.encrypted = false;
    this.readyToSubmit = false;

    // Staff portal ingest
    this.portalEndpoint = (root.getAttribute('data-portal-endpoint') || '').trim();
    this.portalToken = (root.getAttribute('data-portal-token') || '').trim();
    this.portalEnabled = !!(this.portalEndpoint && this.portalToken);
    this.posted = false;

    this.assetMap = this.loadAssetMap();
    this.workingPolicyDefault = (root.getAttribute('data-working-policy') || '').trim();
    this.consolePolicyDefault = (root.getAttribute('data-console-policy') || '').trim();
    this.policyEl = root.querySelector('[data-tradein-policy]');
    this.modalEnabled = root.getAttribute('data-modal-enabled') === 'true';
    this.modal = this.modalEnabled ? root.closest('[data-tradein-modal]') : null;
    this.modalDialog = this.modal ? this.modal.querySelector('.rebx-tradein-modal__dialog') : null;
    this.modalTitleEl = root.querySelector('[data-tradein-modal-title]');
    this.modalTitleDefault = this.modalTitleEl ? this.modalTitleEl.textContent.trim() : '';
    this.categoryLocked = false;
    this.modalReturnFocus = null;

    this.items = [];
    this.uid = 0;
    this.basket = [];
    this.basketUid = 0;
    this.draftId = null;
    this.priceAnchor = null;
    this.quote = null;
    this.catalogLoading = true;
    this.groupList = [];
    this.groupMap = {};
    this.modelQty = {};
    this.creditRate = (parseFloat(root.getAttribute('data-credit-rate')) || 60) / 100;
    this.cashRate = (parseFloat(root.getAttribute('data-cash-rate')) || 55) / 100;
    this.creditRateStock = (parseFloat(root.getAttribute('data-credit-rate-stock')) || 40) / 100;
    this.cashRateStock = (parseFloat(root.getAttribute('data-cash-rate-stock')) || 35) / 100;
    this.stockThreshold = parseInt(root.getAttribute('data-stock-threshold'), 10) || 3;
    this.catalogHandles = (root.getAttribute('data-catalog') || '')
      .split(',')
      .map(function (handle) { return handle.trim(); })
      .filter(Boolean);

    if (this.drop && this.input) {
      this.bind();
      this.render();
    }

    if (this.payRadios.length && this.payGroups.length) {
      this.initPayment();
    }

    if (this.categoryRadios.length && this.deviceFields.length) {
      this.initDevice();
    }

    if (this.wizardEnabled && this.steps.length > 1) {
      this.initWizard();
    }

    if (this.modalEnabled) {
      this.initModal();
    }

    this.initPricing();
  }

  /* ── Device-aware questions ──────────────────────────────────────────── */
  TradeInForm.prototype.loadProfiles = function () {
    var profiles = {};
    Object.keys(DEFAULT_PROFILES).forEach(function (k) {
      profiles[k] = DEFAULT_PROFILES[k];
    });
    var script = this.root.querySelector('[data-device-config]');
    if (script && script.textContent.trim()) {
      try {
        var override = JSON.parse(script.textContent);
        Object.keys(override).forEach(function (k) {
          var base = profiles[k] || {};
          var merged = {};
          Object.keys(base).forEach(function (p) { merged[p] = base[p]; });
          Object.keys(override[k]).forEach(function (p) { merged[p] = override[k][p]; });
          profiles[k] = merged;
        });
      } catch (err) {
        /* keep defaults on bad JSON */
      }
    }
    return profiles;
  };

  TradeInForm.prototype.loadAssetMap = function () {
    var script = this.root.querySelector('[data-tradein-asset-map]');
    if (!script || !script.textContent.trim()) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      return {};
    }
  };

  TradeInForm.prototype.assetUrl = function (filename) {
    return (this.assetMap && this.assetMap[filename]) || '';
  };

  TradeInForm.prototype.initDevice = function () {
    var self = this;

    // Guided make → model elements.
    this.makeFieldset = this.findChoice('Make');
    this.modelChoice = this.root.querySelector('[data-model-choice]');
    this.modelGrid = this.root.querySelector('[data-model-grid]');
    this.modelTextWrap = this.root.querySelector('[data-model-text-wrap]');
    this.modelTextInput = this.root.querySelector('[data-model-text-input]');
    this.modelTextLabel = this.root.querySelector('[data-model-text-label]');
    this.modelHidden = this.root.querySelector('[data-model-value]');
    this.modelTextLabelDefault = this.modelTextLabel ? this.modelTextLabel.textContent : 'Make & model';

    if (this.makeFieldset) {
      this.makeFieldset.addEventListener('change', function (e) {
        if (e.target && e.target.name === 'contact[Make]') self.onMakeChange();
      });
    }
    if (this.modelChoice) {
      this.modelChoice.addEventListener('change', function (e) {
        if (e.target && e.target.hasAttribute('data-model-input')) self.onModelChange();
      });
    }
    if (this.modelTextInput) {
      this.modelTextInput.addEventListener('input', function () {
        if (!self.modelTextInput.disabled && self.modelHidden) {
          self.modelHidden.value = self.modelTextInput.value;
        }
      });
      // Advance once the shopper leaves the free-text model field with content.
      this.modelTextInput.addEventListener('change', function () {
        self.forceActive = null;
        self.refreshFlow();
      });
    }
    // Optional single-value fields advance the flow once filled and blurred.
    var batteryInput = this.root.querySelector('input[name="contact[Battery health %]"]');
    [this.processorInput, this.imeiInput, batteryInput].forEach(function (inp) {
      if (!inp) return;
      inp.addEventListener('change', function () {
        self.forceActive = null;
        self.refreshFlow();
      });
    });

    // Progressive disclosure: build an ordered registry of the device taxonomy
    // questions and reveal them one at a time, collapsing answered ones to chips.
    this.buildSlotRegistry();

    this.categoryRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (radio.checked) self.applyDevice(self.kindFor(radio));
      });
    });
    var checked = null;
    this.categoryRadios.forEach(function (radio) {
      if (radio.checked) checked = radio;
    });
    if (!this.modalEnabled || checked) {
      this.applyDevice(checked ? this.kindFor(checked) : '');
    }
  };

  TradeInForm.prototype.initModal = function () {
    var self = this;
    if (!this.modal) return;

    this.modal.querySelectorAll('[data-tradein-modal-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.closeModal();
      });
    });

    this._onModalKeydown = function (e) {
      if (e.key === 'Escape' && self.isModalOpen()) self.closeModal();
    };
    document.addEventListener('keydown', this._onModalKeydown);
  };

  TradeInForm.prototype.isModalOpen = function () {
    return !!(this.modal && !this.modal.hidden);
  };

  TradeInForm.prototype.openModal = function (kind, label) {
    if (!this.modal) return;
    if (document.__rebxTradeInFunnel) document.__rebxTradeInFunnel.onFunnelStart();
    this.modalReturnFocus = document.activeElement;
    this.resetTradeIn();
    this.categoryLocked = !!kind;
    if (kind) {
      this.selectCategoryByKind(kind, label || '');
    } else {
      this.updateModalTitle('');
    }
    this.modal.hidden = false;
    this.modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('rebx-tradein-modal-open');
    if (this.wizardEnabled && this.steps.length) this.goTo(0, true);
    this.refreshFlow();
    if (this.modalDialog && this.modalDialog.focus) {
      try { this.modalDialog.focus({ preventScroll: true }); } catch (err) { this.modalDialog.focus(); }
    }
  };

  TradeInForm.prototype.closeModal = function () {
    if (!this.modal) return;
    if (document.__rebxTradeInFunnel && !this.posted) document.__rebxTradeInFunnel.onFunnelReset();
    this.modal.hidden = true;
    this.modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('rebx-tradein-modal-open');
    this.categoryLocked = false;
    if (this.modalReturnFocus && this.modalReturnFocus.focus) {
      try { this.modalReturnFocus.focus({ preventScroll: true }); } catch (err) { this.modalReturnFocus.focus(); }
    }
    this.modalReturnFocus = null;
  };

  TradeInForm.prototype.updateModalTitle = function (label) {
    if (!this.modalTitleEl) return;
    if (label) {
      this.modalTitleEl.textContent = 'Trade in your ' + String(label).toLowerCase();
      return;
    }
    this.modalTitleEl.textContent = this.modalTitleDefault || 'Start your trade-in';
  };

  TradeInForm.prototype.selectCategoryByKind = function (kind, label) {
    var match = null;
    var self = this;
    this.categoryRadios.forEach(function (radio) {
      if ((radio.getAttribute('data-device-kind') || '').trim().toLowerCase() === kind) {
        match = radio;
      }
    });
    if (!match) return;
    match.checked = true;
    this.applyDevice(kind);
    if (!label) {
      var titleEl = match.parentNode && match.parentNode.querySelector('.rebx-tradein-card__title');
      label = titleEl ? titleEl.textContent.trim() : '';
    }
    this.updateModalTitle(label);
    this.forceActive = null;
    this.refreshFlow();
  };

  TradeInForm.prototype.resetTradeIn = function () {
    this.categoryLocked = false;
    this.forceActive = null;
    this.setError('');
    if (this.form && typeof this.form.reset === 'function') this.form.reset();
    if (this.modelHidden) this.modelHidden.value = '';
    if (this.processorInput) this.processorInput.value = '';
    while (this.items.length) {
      this.removeItem(this.items[0].id);
    }
    Object.keys(this.slotMap || {}).forEach(function (k) {
      this.slotMap[k].skipped = false;
    }, this);
    this.applyDevice('');
    if (this.wizardEnabled && this.steps.length) this.goTo(0, true);
    this.refreshFlow();
    this.updateModalTitle('');
    this.priceAnchor = null;
    this.draftId = null;
    this.quote = null;
    if (typeof this.renderBasket === 'function') this.renderBasket();
  };

  /* ── Progressive disclosure (step 1 device taxonomy) ─────────────────── */
  function slotLabelOf(el) {
    if (!el) return '';
    var l =
      el.querySelector('[data-choice-label]') ||
      el.querySelector('legend span') ||
      el.querySelector('.rebx-tradein-field__label span') ||
      el.querySelector('.rebx-tradein-field__label');
    return l ? l.textContent.trim() : '';
  }

  // Build a per-step registry of taxonomy questions. Every step grid flagged
  // `.is-progressive` is scanned in document order; each device field becomes a
  // "slot" that can be locked (hidden), active (expanded) or done (a chip).
  TradeInForm.prototype.buildSlotRegistry = function () {
    var self = this;
    this.slotMap = {};
    this.progSteps = [];

    var grids = Array.prototype.slice.call(
      this.root.querySelectorAll('.rebx-tradein-step__grid.is-progressive')
    );

    grids.forEach(function (grid) {
      // Summary rail holds the answered-question pills.
      var rail = document.createElement('div');
      rail.className = 'rebx-tradein-rail';
      rail.setAttribute('data-picked-rail', '');
      rail.hidden = true;
      grid.insertBefore(rail, grid.firstChild);

      var step = { grid: grid, rail: rail, slots: [], flow: [] };
      var fields = Array.prototype.slice.call(grid.querySelectorAll(':scope > [data-device-field]'));

      fields.forEach(function (el) {
        var key = el.getAttribute('data-device-field');
        if (key === 'modelText') return; // folded into the model slot

        var primary = el;
        var els = [el];
        if (key === 'model') els = [el, self.modelTextWrap].filter(Boolean);

        var required;
        if (key === 'model') required = true;
        else if (el.classList.contains('rebx-tradein-choice')) {
          required = el.getAttribute('data-choice-required') === 'true';
        } else {
          required = false; // free-text / number / checkbox extras are optional
        }

        var label = slotLabelOf(primary);
        primary.classList.add('rebx-tradein-slot');

        // Pill shown in the rail once the question is answered or skipped.
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'rebx-tradein-pill';
        chip.setAttribute('data-slot-edit', '');
        chip.hidden = true;
        chip.innerHTML =
          '<span class="rebx-tradein-pill__label">' + escapeHtml(label) + '</span>' +
          '<span class="rebx-tradein-pill__value" data-slot-value></span>' +
          '<span class="rebx-tradein-pill__edit" aria-hidden="true">✎</span>';
        rail.appendChild(chip);

        var slot = {
          key: key, primary: primary, els: els, label: label,
          required: required, summary: chip, skipped: false, skip: null
        };

        // Optional questions get a Skip control (only while empty + active).
        if (!required) {
          var skip = document.createElement('button');
          skip.type = 'button';
          skip.className = 'rebx-tradein-skip';
          skip.setAttribute('data-slot-skip', '');
          skip.hidden = true;
          skip.textContent = 'Skip for now';
          primary.appendChild(skip);
          skip.addEventListener('click', function () {
            slot.skipped = true;
            self.forceActive = null;
            self.refreshFlow();
          });
          slot.skip = skip;
        }

        chip.addEventListener('click', function () { self.editSlot(slot); });
        self.slotMap[key] = slot;
        step.slots.push(slot);
      });

      // Choice answers advance the flow. category/make own dedicated handlers.
      grid.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || !t.matches || !t.matches('input[data-choice-input]')) return;
        var fs = t.closest('[data-device-field]');
        var key = fs && fs.getAttribute('data-device-field');
        if (key === 'category' || key === 'make') return;
        self.forceActive = null;
        self.refreshFlow();
      });

      self.progSteps.push(step);
    });
  };

  TradeInForm.prototype.slotSatisfied = function (slot) {
    if (slot.required) return this.slotComplete(slot);
    return slot.skipped || this.slotComplete(slot);
  };

  TradeInForm.prototype.setEl = function (el, visible, keepEnabled) {
    if (!el) return;
    el.hidden = !visible;
    var disable = !visible && !keepEnabled;
    if (el.tagName && el.tagName.toLowerCase() === 'fieldset') {
      el.disabled = disable;
    } else {
      var c = el.querySelectorAll('input, select, textarea');
      Array.prototype.forEach.call(c, function (x) { x.disabled = disable; });
    }
  };

  // Recompute which slots apply to the current device, per step.
  TradeInForm.prototype.buildFlow = function () {
    var self = this;
    var profile = this.activeProfile || {};
    var shown = {};
    (profile.fields || []).forEach(function (f) { shown[f] = true; });
    var hasMakes = !!(profile.makes && Object.keys(profile.makes).length);

    (this.progSteps || []).forEach(function (step) {
      step.flow = step.slots.filter(function (slot) {
        var key = slot.key;
        if (key === 'category') return true;
        if (!self.activeKind) return false;
        if (key === 'make') return hasMakes;
        if (key === 'model') return true;
        return !!shown[key];
      });
    });
  };

  TradeInForm.prototype.slotComplete = function (slot) {
    if (slot.key === 'model') return !!(this.modelHidden && this.modelHidden.value.trim());
    if (slot.primary.querySelector('input[type=radio]')) {
      return !!slot.primary.querySelector('input[type=radio]:checked');
    }
    var checks = slot.primary.querySelectorAll('input[type=checkbox]');
    if (checks.length) {
      return Array.prototype.some.call(checks, function (c) { return c.checked; });
    }
    var field = slot.primary.querySelector('input, textarea, select');
    return !!(field && String(field.value).trim());
  };

  TradeInForm.prototype.slotValue = function (slot) {
    if (slot.key === 'model') return this.modelHidden ? this.modelHidden.value : '';
    var checked = slot.primary.querySelector('input[type=radio]:checked');
    if (checked) {
      var title = checked.parentNode && checked.parentNode.querySelector('.rebx-tradein-card__title');
      return title ? title.textContent : checked.value;
    }
    var ticked = slot.primary.querySelectorAll('input[type=checkbox]:checked');
    if (ticked.length) {
      return Array.prototype.map.call(ticked, function (c) {
        var s = c.parentNode && c.parentNode.querySelector('span');
        return s ? s.textContent : c.value;
      }).join(', ');
    }
    var field = slot.primary.querySelector('input, textarea, select');
    return field ? field.value : '';
  };

  TradeInForm.prototype.setModelRequired = function (req) {
    if (!this.modelChoice) return;
    var rs = this.modelChoice.querySelectorAll('input[type=radio][data-model-input]');
    Array.prototype.forEach.call(rs, function (r) {
      if (req) r.setAttribute('required', 'required');
      else r.removeAttribute('required');
    });
  };

  // states: 'active' (the one expanded question) | 'done' (answered → pill)
  //         | 'skipped' (optional, skipped → muted pill) | 'locked' (hidden)
  TradeInForm.prototype.setSlotState = function (slot, state) {
    var self = this;

    if (slot.key === 'category' && this.categoryLocked) {
      slot.els.forEach(function (el) { self.setEl(el, false, true); });
      if (slot.skip) slot.skip.hidden = true;
      if (slot.summary) {
        var done = this.slotComplete(slot);
        slot.summary.hidden = !done;
        slot.summary.classList.remove('is-skipped');
        slot.summary.disabled = true;
        var valEl = slot.summary.querySelector('[data-slot-value]');
        if (valEl) valEl.textContent = done ? (this.slotValue(slot) || '—') : '';
      }
      return;
    }

    if (slot.summary) slot.summary.disabled = false;

    slot.els.forEach(function (el) { if (el) el.classList.remove('is-active'); });

    // Skip control: only while the optional question is active and still empty.
    if (slot.skip) {
      slot.skip.hidden = !(state === 'active' && !this.slotComplete(slot));
    }

    // Rail pill: shown for answered or skipped questions.
    if (slot.summary) {
      if (state === 'done' || state === 'skipped') {
        slot.summary.hidden = false;
        slot.summary.classList.toggle('is-skipped', state === 'skipped');
        var v = slot.summary.querySelector('[data-slot-value]');
        if (v) v.textContent = state === 'skipped' ? 'Skipped' : (this.slotValue(slot) || '—');
      } else {
        slot.summary.hidden = true;
      }
    }

    if (slot.key === 'model') {
      this.setModelRequired(false);
      if (this.modelTextInput) this.modelTextInput.required = false;
      if (state === 'active') {
        if (this.modelMode === 'text') {
          this.setEl(this.modelChoice, false, false);
          this.setEl(this.modelTextWrap, true);
          if (this.modelTextWrap) this.modelTextWrap.classList.add('is-active');
          if (this.modelTextInput) this.modelTextInput.required = true;
        } else {
          this.setEl(this.modelTextWrap, false, false);
          this.setEl(this.modelChoice, true);
          this.modelChoice.classList.add('is-active');
          this.setModelRequired(true);
        }
      } else if (state === 'done') {
        this.setEl(this.modelChoice, false, true);
        this.setEl(this.modelTextWrap, false, true);
      } else {
        this.setEl(this.modelChoice, false, false);
        this.setEl(this.modelTextWrap, false, false);
      }
      return;
    }

    if (state === 'active') {
      slot.els.forEach(function (el) { self.setEl(el, true); el.classList.add('is-active'); });
    } else if (state === 'done' || state === 'skipped') {
      slot.els.forEach(function (el) { self.setEl(el, false, true); });
    } else {
      slot.els.forEach(function (el) { self.setEl(el, false, false); });
    }
  };

  TradeInForm.prototype.refreshFlow = function () {
    if (!this.progSteps) return;
    var self = this;
    this.buildFlow();
    this.progSteps.forEach(function (step) { self.refreshStep(step); });
  };

  // Within one step exactly ONE question is expanded at a time. The active
  // question is the first unsatisfied slot (required → must answer; optional →
  // answer or skip). Everything before it shows as a pill; everything after is
  // hidden until it's reached.
  TradeInForm.prototype.refreshStep = function (step) {
    var self = this;
    var flow = step.flow || [];
    var inFlow = {};
    flow.forEach(function (s) { inFlow[s.key] = true; });

    // Slots that don't apply to this device stay hidden.
    step.slots.forEach(function (s) {
      if (!inFlow[s.key]) self.setSlotState(s, 'locked');
    });

    var activeIndex = -1;
    for (var i = 0; i < flow.length; i++) {
      if (!self.slotSatisfied(flow[i])) { activeIndex = i; break; }
    }
    if (this.forceActive) {
      var fi = flow.indexOf(this.forceActive);
      if (fi >= 0) activeIndex = fi;
    }

    flow.forEach(function (slot, i) {
      var state;
      if (i === activeIndex) state = 'active';
      else if (self.slotSatisfied(slot)) state = self.slotComplete(slot) ? 'done' : 'skipped';
      else state = 'locked';
      self.setSlotState(slot, state);
    });

    // Rail is only visible when at least one pill is showing.
    if (step.rail) {
      var any = false;
      flow.forEach(function (s) { if (s.summary && !s.summary.hidden) any = true; });
      step.rail.hidden = !any;
    }
  };

  TradeInForm.prototype.editSlot = function (slot) {
    slot.skipped = false; // re-opening a skipped question lets them answer it
    this.forceActive = slot;
    this.refreshFlow();
    var f = slot.primary.querySelector('input:not([type=hidden]), select, textarea');
    if (slot.key === 'model' && this.modelMode === 'text' && this.modelTextInput) f = this.modelTextInput;
    if (f && f.focus) { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } }
  };

  TradeInForm.prototype.kindFor = function (radio) {
    var explicit = (radio.getAttribute('data-device-kind') || '').trim().toLowerCase();
    if (explicit) return explicit;
    return inferKind(radio.value || '');
  };

  TradeInForm.prototype.applyDevice = function (kind) {
    var self = this;
    var profile;
    if (!kind) {
      profile = { fields: [] };
    } else {
      profile = this.profiles[kind] || this.profiles.other || { fields: ['model'] };
    }
    this.activeKind = kind || '';

    var shown = {};
    (profile.fields || []).forEach(function (f) { shown[f] = true; });

    // Swap option sets for the data-driven choice groups.
    this.setChoiceOptions('Storage capacity', profile.storage);
    this.setChoiceOptions('Connectivity', profile.connectivity);
    this.setChoiceOptions('RAM', profile.ram);
    this.setChoiceOptions('Case size', profile.caseSize);
    this.setAccessories(profile.accessories);

    if (profile.condition) this.setChoiceOptions('Condition', profile.condition);
    if (profile.screen) this.setChoiceOptions('Screen condition', profile.screen);
    if (profile.body) this.setChoiceOptions('Body condition', profile.body);
    this.applyGradeHints(profile.gradeHints || WORKING_GRADE_HINTS);
    this.updateWorkingPolicy(kind);

    // Relabel / re-help the identifier (IMEI vs serial) per device.
    if (this.imeiLabelEl) {
      this.imeiLabelEl.textContent = profile.imeiLabel || this.imeiLabelDefault;
    }
    if (this.imeiHintEl) {
      this.imeiHintEl.textContent = profile.imeiHelp || this.imeiHintDefault;
    }
    if (this.imeiInput) {
      // Optional by default — IMEI/serial is hard to find for most people.
      // A profile can opt back in with imeiRequired:true.
      if (profile.imeiRequired === true && shown.imei) {
        this.imeiInput.setAttribute('required', 'required');
      } else {
        this.imeiInput.removeAttribute('required');
      }
    }

    // Every device field that's a registered progressive slot (both steps) has
    // its visibility owned by the flow. Any field that isn't a slot still
    // toggles the classic way.
    this.deviceFields.forEach(function (el) {
      var key = el.getAttribute('data-device-field');
      if (key === 'modelText' || (self.slotMap && self.slotMap[key])) return;
      self.toggleField(el, !!shown[key]);
    });

    // Switching device must drop any stale answers from the previous one.
    ['Make', 'Storage capacity', 'Connectivity', 'Case size', 'RAM',
     'Condition', 'Screen condition', 'Body condition', 'Network status'].forEach(function (f) {
      var fs = self.findChoice(f);
      if (fs) self.clearField(fs);
    });
    if (this.processorInput) this.processorInput.value = '';

    // Reset "skipped" so optional questions are offered again for the new device.
    Object.keys(this.slotMap || {}).forEach(function (k) { self.slotMap[k].skipped = false; });

    this.setupModel(profile);
    this.forceActive = null;
    this.refreshFlow();
  };

  /* ── Guided make → model ─────────────────────────────────────────────── */
  TradeInForm.prototype.setupModel = function (profile) {
    this.activeProfile = profile || {};
    var makes = profile && profile.makes;
    if (this.modelHidden) this.modelHidden.value = '';
    if (this.modelTextInput) {
      this.modelTextInput.value = '';
      this.modelTextInput.required = false;
      if (profile && profile.modelPlaceholder) {
        this.modelTextInput.placeholder = profile.modelPlaceholder;
      }
    }
    if (this.modelGrid) this.modelGrid.innerHTML = '';

    // Nothing chosen yet → make/model excluded from the flow entirely.
    if (!this.activeKind) {
      this.modelMode = 'choice';
      return;
    }

    if (makes && typeof makes === 'object' && Object.keys(makes).length) {
      this.setChoiceOptions('Make', Object.keys(makes).concat(['Other brand']));
      this.modelMode = 'choice';
      if (this.modelTextLabel) this.modelTextLabel.textContent = 'Tell us the exact model';
    } else {
      // No brand data for this device → straight to free text.
      this.modelMode = 'text';
      if (this.modelTextInput) this.modelTextInput.required = true;
      if (this.modelTextLabel) this.modelTextLabel.textContent = this.modelTextLabelDefault;
    }
  };

  TradeInForm.prototype.onMakeChange = function () {
    var make = this.selectedValue('contact[Make]');
    var makes = this.activeProfile && this.activeProfile.makes;
    var models = makes && makes[make];
    this.buildModels(models);
    this.applyModelSpecs(null);
    this.forceActive = null;
    this.refreshFlow();
  };

  TradeInForm.prototype.buildModels = function (models) {
    if (this.modelHidden) this.modelHidden.value = '';
    if (models && models.length && this.modelGrid) {
      var html = models
        .map(function (m) {
          return modelCardHTML(m, m);
        })
        .join('');
      html += modelCardHTML('__other__', 'I can’t find my model');
      this.modelGrid.innerHTML = html;
      this.modelMode = 'choice';
      if (this.modelTextInput) this.modelTextInput.required = false;
    } else {
      // "Other brand" or no list → free text.
      if (this.modelGrid) this.modelGrid.innerHTML = '';
      this.modelMode = 'text';
      if (this.modelTextInput) this.modelTextInput.required = true;
    }
  };

  TradeInForm.prototype.onModelChange = function () {
    var self = this;
    var picked = this.selectedValue('rebx_model_choice');
    if (picked === '__other__') {
      this.modelMode = 'text';
      if (this.modelTextInput) {
        this.modelTextInput.required = true;
        if (this.modelHidden) this.modelHidden.value = this.modelTextInput.value;
      }
      this.applyModelSpecs(null);
      this.forceActive = null;
      this.refreshFlow();
      setTimeout(function () {
        if (self.modelTextInput) { try { self.modelTextInput.focus(); } catch (e) {} }
      }, 0);
    } else {
      this.modelMode = 'choice';
      if (this.modelTextInput) this.modelTextInput.required = false;
      if (this.modelHidden) this.modelHidden.value = picked;
      this.applyModelSpecs(picked);
      this.forceActive = null;
      this.refreshFlow();
    }
  };

  // Refine storage / case size / connectivity / RAM to the exact model picked.
  // Falls back to the device-level defaults for any model we don't have specs for.
  TradeInForm.prototype.applyModelSpecs = function (model) {
    var self = this;
    var profile = this.activeProfile || {};
    var spec = (model && profile.specs && profile.specs[model]) || {};
    var groups = [
      ['storage', 'Storage capacity'],
      ['caseSize', 'Case size'],
      ['connectivity', 'Connectivity'],
      ['ram', 'RAM']
    ];
    groups.forEach(function (g) {
      var key = g[0];
      var opts = spec[key] || profile[key];
      if (!opts) return;
      if (key === 'storage' && opts[0] !== 'Not sure') {
        opts = ['Not sure'].concat(opts);
      }
      self.setChoiceOptions(g[1], opts);
    });
  };

  TradeInForm.prototype.selectedValue = function (name) {
    if (!this.form) return '';
    var rs = this.form.querySelectorAll('input[type=radio][name="' + name + '"]');
    var v = '';
    Array.prototype.forEach.call(rs, function (r) {
      if (r.checked) v = r.value;
    });
    return v;
  };

  function modelCardHTML(value, title) {
    return (
      '<label class="rebx-tradein-card">' +
      '<input type="radio" class="rebx-tradein-card__input" name="rebx_model_choice" value="' +
      escapeHtml(value) +
      '" data-model-input>' +
      '<span class="rebx-tradein-card__body"><span class="rebx-tradein-card__title">' +
      escapeHtml(title) +
      '</span></span>' +
      '<span class="rebx-tradein-card__tick" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="m5 12 5 5 9-10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg></span>' +
      '</label>'
    );
  }

  TradeInForm.prototype.toggleField = function (el, show) {
    if (!el) return;
    el.hidden = !show;
    if (el.tagName && el.tagName.toLowerCase() === 'fieldset') {
      el.disabled = !show;
    } else {
      var controls = el.querySelectorAll('input, select, textarea');
      Array.prototype.forEach.call(controls, function (c) {
        c.disabled = !show;
      });
    }
    if (!show) this.clearField(el);
  };

  TradeInForm.prototype.clearField = function (el) {
    if (!el) return;
    var controls = el.querySelectorAll('input, select, textarea');
    Array.prototype.forEach.call(controls, function (c) {
      if (c.type === 'radio' || c.type === 'checkbox') {
        c.checked = false;
      } else if (c.type !== 'hidden') {
        c.value = '';
      }
    });
  };

  TradeInForm.prototype.findChoice = function (fieldName) {
    return this.root.querySelector('[data-choice-field="' + fieldName + '"]');
  };

  TradeInForm.prototype.setChoiceOptions = function (fieldName, options) {
    if (!options) return; // no override → keep the Liquid fallback options
    var fieldset = this.findChoice(fieldName);
    if (!fieldset) return;
    var grid = fieldset.querySelector('[data-choice-grid]');
    if (!grid) return;
    var required = fieldset.getAttribute('data-choice-required') === 'true';
    var visual = fieldset.classList.contains('rebx-tradein-choice--visual');
    var self = this;
    grid.innerHTML = options
      .map(function (opt) {
        return choiceCardHTML(fieldName, opt, required, visual, self);
      })
      .join('');
  };

  TradeInForm.prototype.setChoiceHint = function (fieldName, text) {
    var fieldset = this.findChoice(fieldName);
    if (!fieldset) return;
    var hint = fieldset.querySelector('[data-choice-hint]');
    if (!hint) return;
    if (text) {
      hint.textContent = text;
      hint.hidden = false;
    } else {
      hint.textContent = '';
      hint.hidden = true;
    }
  };

  TradeInForm.prototype.applyGradeHints = function (hints) {
    hints = hints || {};
    this.setChoiceHint('Condition', hints.condition || '');
    this.setChoiceHint('Screen condition', hints.screen || '');
    this.setChoiceHint('Body condition', hints.body || '');
  };

  TradeInForm.prototype.updateWorkingPolicy = function (kind) {
    if (!this.policyEl) return;
    if (!kind) {
      this.policyEl.hidden = true;
      this.policyEl.textContent = '';
      return;
    }
    if (kind === 'console') {
      this.policyEl.textContent = this.consolePolicyDefault || CONSOLE_GRADE_HINTS.condition;
      this.policyEl.hidden = !this.policyEl.textContent;
      return;
    }
    this.policyEl.textContent = this.workingPolicyDefault || WORKING_GRADE_HINTS.condition;
    this.policyEl.hidden = !this.policyEl.textContent;
  };

  TradeInForm.prototype.violatesWorkingPolicy = function () {
    if (this.activeKind === 'console' || !this.activeKind) return false;
    var answers = [
      this.fieldValue('Condition'),
      this.fieldValue('Screen condition'),
      this.fieldValue('Body condition')
    ].join(' ');
    return NON_WORKING_RE.test(answers);
  };

  TradeInForm.prototype.setAccessories = function (options) {
    if (!options || !this.accessoriesGrid) return;
    this.accessoriesGrid.innerHTML = options
      .map(function (opt) {
        var v = String(opt).trim();
        return (
          '<label class="rebx-tradein-check">' +
          '<input type="checkbox" name="contact[Included items][]" value="' +
          escapeHtml(v) +
          '">' +
          '<span>' +
          escapeHtml(v) +
          '</span></label>'
        );
      })
      .join('');
  };

  function parseChoiceOption(opt) {
    var v = String(opt).trim();
    var assetName = '';
    var pipeIdx = v.lastIndexOf('|');
    if (pipeIdx !== -1) {
      var token = v.slice(pipeIdx + 1).trim();
      if (token.indexOf('asset:') === 0) {
        assetName = token.slice(6).trim();
        v = v.slice(0, pipeIdx).trim();
      }
    }
    var parts = v.split(' — ');
    return {
      cardValue: v,
      title: parts[0].trim(),
      sub: parts.length > 1 ? parts.slice(1).join(' — ').trim() : '',
      assetName: assetName
    };
  }

  function choiceCardHTML(fieldName, opt, required, visual, form) {
    var parsed = parseChoiceOption(opt);
    var media = '';
    if (parsed.assetName && form && typeof form.assetUrl === 'function') {
      var src = form.assetUrl(parsed.assetName);
      if (src) {
        media =
          '<span class="rebx-tradein-card__media">' +
          '<img src="' +
          escapeHtml(src) +
          '" alt="" class="rebx-tradein-card__img" loading="lazy" width="400" height="280">' +
          '</span>';
      }
    }
    var cardClass = 'rebx-tradein-card' + (media ? ' rebx-tradein-card--media' : '') + (visual ? ' rebx-tradein-card--visual' : '');
    return (
      '<label class="' +
      cardClass +
      '">' +
      '<input type="radio" class="rebx-tradein-card__input" name="contact[' +
      fieldName +
      ']" value="' +
      escapeHtml(parsed.cardValue) +
      '" data-choice-input' +
      (required ? ' required' : '') +
      '>' +
      media +
      '<span class="rebx-tradein-card__body">' +
      '<span class="rebx-tradein-card__title">' +
      escapeHtml(parsed.title) +
      '</span>' +
      (parsed.sub ? '<span class="rebx-tradein-card__sub">' + escapeHtml(parsed.sub) + '</span>' : '') +
      '</span>' +
      '<span class="rebx-tradein-card__tick" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="m5 12 5 5 9-10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg></span>' +
      '</label>'
    );
  }

  function inferKind(text) {
    var t = String(text).toLowerCase();
    if (/watch|wearable|band/.test(t)) return 'watch';
    if (/laptop|macbook|notebook|chromebook/.test(t)) return 'laptop';
    if (/ipad|\btablet\b|\btab\b/.test(t)) return 'tablet';
    if (/console|playstation|\bps[45]\b|xbox|nintendo|switch/.test(t)) return 'console';
    if (/head|speaker|audio|earbud|airpod|earphone|buds/.test(t)) return 'audio';
    if (/phone|iphone|pixel|galaxy/.test(t)) return 'phone';
    return 'other';
  }

  /* ── Conditional payment fields ──────────────────────────────────────── */
  TradeInForm.prototype.initPayment = function () {
    var self = this;
    this.payRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        self.updatePayment();
      });
    });
    this.updatePayment();
  };

  TradeInForm.prototype.updatePayment = function () {
    var target = '';
    this.payRadios.forEach(function (radio) {
      if (radio.checked) target = radio.getAttribute('data-pay-target') || '';
    });

    this.payGroups.forEach(function (group) {
      var match = group.getAttribute('data-pay-group') === target;
      group.hidden = !match;
      var fields = group.querySelectorAll('[data-pay-required]');
      Array.prototype.forEach.call(fields, function (el) {
        if (match) {
          el.setAttribute('required', 'required');
        } else {
          el.removeAttribute('required');
        }
      });
    });
  };

  /* ── Wizard ──────────────────────────────────────────────────────────── */
  TradeInForm.prototype.initWizard = function () {
    var self = this;
    this.root.classList.add('is-wizard');
    if (this.progress) this.progress.hidden = false;
    if (this.stepTotalEl) this.stepTotalEl.textContent = this.steps.length;

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        if (self.validateStep(self.current)) {
          if (self.steps[self.current] && self.steps[self.current].hasAttribute('data-offer-step')) self.commitDraft();
          self.goTo(self.current + 1);
        }
      });
    }
    if (this.backBtn) {
      this.backBtn.addEventListener('click', function () {
        self.goTo(self.current - 1);
      });
    }

    // Pressing Enter in a text field advances instead of submitting early.
    this.root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'textarea') return;
      if (self.current < self.steps.length - 1 && self.root.contains(e.target)) {
        e.preventDefault();
        if (self.validateStep(self.current)) {
          if (self.steps[self.current] && self.steps[self.current].hasAttribute('data-offer-step')) self.commitDraft();
          self.goTo(self.current + 1);
        }
      }
    });

    this.goTo(0, true);
  };

  TradeInForm.prototype.goTo = function (index, silent) {
    if (index < 0 || index >= this.steps.length) return;
    this.current = index;

    this.steps.forEach(function (step, i) {
      step.classList.toggle('is-active', i === index);
      step.hidden = i !== index;
    });

    var isLast = index === this.steps.length - 1;
    var isFirst = index === 0;

    if (this.backBtn) this.backBtn.hidden = isFirst;
    if (this.nextBtn) this.nextBtn.hidden = isLast;
    if (this.submitBtn) this.submitBtn.hidden = !isLast;

    var pct = Math.round(((index + 1) / this.steps.length) * 100);
    if (this.bar) this.bar.style.width = pct + '%';
    if (this.stepCurrentEl) this.stepCurrentEl.textContent = index + 1;
    if (this.stepNameEl) {
      this.stepNameEl.textContent = this.steps[index].getAttribute('data-step-title') || '';
    }

    if (this.steps[index].hasAttribute('data-offer-step') && this.refreshOffer) this.refreshOffer();
    if (this.steps[index].querySelector('[data-payment]') && this.syncPayoutToPayment) this.syncPayoutToPayment();

    if (isLast) this.buildReview();

    if (!silent) {
      var scrollTarget = this.modalDialog || this.root.querySelector('.rebx-tradein-form__card');
      if (scrollTarget && scrollTarget.scrollTo) {
        scrollTarget.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (scrollTarget && scrollTarget.scrollIntoView) {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      var focusEl = this.steps[index].querySelector(
        'input:not([type=hidden]):not([type=file]), select, textarea, [data-tradein-drop]'
      );
      if (focusEl && focusEl.focus) {
        try { focusEl.focus({ preventScroll: true }); } catch (err) { focusEl.focus(); }
      }
    }
  };

  TradeInForm.prototype.validateStep = function (index) {
    var step = this.steps[index];
    if (!step) return true;
    if (step.hasAttribute('data-offer-step') && this.catalogLoading) {
      this.setStatus('Still checking current prices…', true);
      return false;
    }
    var controls = step.querySelectorAll('input, select, textarea');
    var firstInvalid = null;

    Array.prototype.forEach.call(controls, function (el) {
      if (firstInvalid) return;
      if (el.disabled || el.type === 'hidden') return;
      if (typeof el.checkValidity === 'function' && !el.checkValidity()) {
        firstInvalid = el;
      }
    });

    // Photo requirement lives on its own step.
    if (!firstInvalid && step.contains(this.root.querySelector('[data-tradein-photos]') || document.createElement('i'))) {
      if (this.photosRequired && this.items.length === 0) {
        this.setError('Please add at least one photo of your device.');
        if (this.drop) this.drop.focus();
        return false;
      }
    }

    if (firstInvalid) {
      if (typeof firstInvalid.reportValidity === 'function') {
        firstInvalid.reportValidity();
      }
      if (firstInvalid.focus) firstInvalid.focus();
      return false;
    }

    if (this.violatesWorkingPolicy()) {
      this.setError('We only accept fully working devices in this category. Gaming consoles are the exception — pick Gaming console if yours is faulty.');
      return false;
    }

    return true;
  };

  TradeInForm.prototype.buildReview = function () {
    if (!this.reviewList || !this.form) return;
    var rows = [];
    var pairs = [
      ['Device category', 'Device'],
      ['Make', 'Make'],
      ['Device model', 'Model'],
      ['Storage capacity', 'Storage'],
      ['Connectivity', 'Connectivity'],
      ['Case size', 'Case size'],
      ['RAM', 'Memory'],
      ['Processor', 'Processor'],
      ['Condition', 'Condition'],
      ['Screen condition', 'Screen'],
      ['Body condition', 'Body'],
      ['Network status', 'Network'],
      ['Battery health %', 'Battery'],
      ['Payment method', 'Payment'],
      ['ID type', 'ID']
    ];
    var form = this.form;

    pairs.forEach(function (pair) {
      var name = 'contact[' + pair[0] + ']';
      var val = '';
      var radios = form.querySelectorAll('input[type=radio][name="' + name + '"]');
      if (radios.length) {
        Array.prototype.forEach.call(radios, function (r) {
          if (r.checked) val = r.value;
        });
      } else {
        var el = form.querySelector('[name="' + name + '"]');
        if (el) val = el.value;
      }
      if (val) {
        rows.push(
          '<div class="rebx-tradein-review__row"><dt>' +
            pair[1] +
            '</dt><dd>' +
            escapeHtml(val) +
            '</dd></div>'
        );
      }
    });

    if (this.items.length) {
      rows.push(
        '<div class="rebx-tradein-review__row"><dt>Photos</dt><dd>' +
          this.items.length +
          ' added · faster valuation</dd></div>'
      );
    }

    if (this.basket && this.basket.length) {
      var totals = this.basketTotals();
      rows.push(
        '<div class="rebx-tradein-review__row"><dt>Selling items</dt><dd>' +
          this.basket.length +
          (this.basket.length === 1 ? ' device' : ' devices') +
          '</dd></div>'
      );
      if (totals.credit || totals.cash) {
        rows.push(
          '<div class="rebx-tradein-review__row"><dt>Store credit total</dt><dd>' +
            formatPounds(totals.credit) +
            '</dd></div>'
        );
        rows.push(
          '<div class="rebx-tradein-review__row"><dt>Cash total</dt><dd>' +
            formatPounds(totals.cash) +
            '</dd></div>'
        );
      }
    }

    this.reviewList.innerHTML = rows.join('');
    if (this.reviewBox) this.reviewBox.hidden = rows.length === 0;
  };

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  TradeInForm.prototype.bind = function () {
    var self = this;

    this.input.addEventListener('change', function (e) {
      self.addFiles(e.target.files);
      self.input.value = '';
    });

    ['dragenter', 'dragover'].forEach(function (type) {
      self.drop.addEventListener(type, function (e) {
        e.preventDefault();
        self.drop.classList.add('is-dragover');
      });
    });

    ['dragleave', 'dragend', 'drop'].forEach(function (type) {
      self.drop.addEventListener(type, function (e) {
        e.preventDefault();
        if (type === 'dragleave' && self.drop.contains(e.relatedTarget)) return;
        self.drop.classList.remove('is-dragover');
      });
    });

    this.drop.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files) {
        self.addFiles(e.dataTransfer.files);
      }
    });

    this.drop.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        self.input.click();
      }
    });

    if (this.form) {
      this.form.addEventListener('submit', function (e) {
        self.onSubmit(e);
      });
    }
  };

  TradeInForm.prototype.setError = function (msg) {
    if (!this.errorEl) return;
    if (msg) {
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    } else {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  };

  TradeInForm.prototype.addFiles = function (fileList) {
    var self = this;
    this.setError('');
    var files = Array.prototype.slice.call(fileList || []);
    var rejected = [];

    files.forEach(function (file) {
      if (self.items.length >= self.maxPhotos) {
        rejected.push('Max ' + self.maxPhotos + ' photos');
        return;
      }
      var type = (file.type || '').toLowerCase();
      var isImage = type.indexOf('image/') === 0 || ACCEPTED.indexOf(type) !== -1;
      if (!isImage) {
        rejected.push(file.name + ' (not an image)');
        return;
      }
      if (bytesToMb(file.size) > self.maxMb) {
        rejected.push(file.name + ' (over ' + self.maxMb + 'MB)');
        return;
      }
      self.items.push({
        id: ++self.uid,
        file: file,
        url: '',
        status: 'pending'
      });
    });

    if (rejected.length) {
      this.setError('Skipped: ' + rejected.join(', ') + '.');
    }

    this.render();
    if (this.endpoint) {
      this.uploadPending();
    }
  };

  TradeInForm.prototype.removeItem = function (id) {
    this.items = this.items.filter(function (item) {
      return item.id !== id;
    });
    this.setError('');
    this.render();
  };

  TradeInForm.prototype.render = function () {
    var self = this;
    if (!this.thumbs) return;

    this.thumbs.innerHTML = '';
    this.thumbs.hidden = this.items.length === 0;

    this.items.forEach(function (item) {
      var li = document.createElement('li');
      li.className = 'rebx-tradein-thumb';
      if (item.status === 'uploading') li.classList.add('is-uploading');
      if (item.status === 'error') li.classList.add('is-error');

      var img = document.createElement('img');
      img.className = 'rebx-tradein-thumb__img';
      img.alt = item.file.name;
      try {
        img.src = URL.createObjectURL(item.file);
        img.addEventListener('load', function () {
          URL.revokeObjectURL(img.src);
        });
      } catch (err) {
        /* no-op */
      }
      li.appendChild(img);

      if (item.status === 'uploading') {
        var spinner = document.createElement('span');
        spinner.className = 'rebx-tradein-thumb__spinner';
        spinner.setAttribute('aria-hidden', 'true');
        li.appendChild(spinner);
      }

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'rebx-tradein-thumb__remove';
      remove.setAttribute('aria-label', 'Remove ' + item.file.name);
      remove.innerHTML = '&times;';
      remove.addEventListener('click', function () {
        self.removeItem(item.id);
      });
      li.appendChild(remove);

      self.thumbs.appendChild(li);
    });

    this.updateSpeed();
    this.updateFields();
  };

  TradeInForm.prototype.updateSpeed = function () {
    if (!this.speedFill) return;
    var pct = this.items.length > 0 ? this.speedBoost : 0;
    this.speedFill.style.width = pct + '%';
    if (this.speedValue) {
      this.speedValue.textContent = (this.items.length > 0 ? '+' : '') + pct + '%';
    }
    var speedEl = this.root.querySelector('[data-tradein-speed]');
    if (speedEl) speedEl.classList.toggle('is-active', this.items.length > 0);
  };

  TradeInForm.prototype.updateFields = function () {
    if (this.summaryField) {
      if (this.items.length === 0) {
        this.summaryField.value = 'No photos added';
      } else {
        var names = this.items.map(function (item) {
          return item.file.name;
        });
        this.summaryField.value = this.items.length + ' photo(s): ' + names.join(', ');
      }
    }
    if (this.linksField) {
      var links = this.items
        .filter(function (item) {
          return item.url;
        })
        .map(function (item) {
          return item.url;
        });
      this.linksField.value = links.join('\n');
    }
  };

  TradeInForm.prototype.uploadPending = function () {
    var self = this;
    this.items.forEach(function (item) {
      if (item.status === 'pending') {
        self.uploadOne(item);
      }
    });
  };

  TradeInForm.prototype.uploadOne = function (item) {
    var self = this;
    item.status = 'uploading';
    this.render();
    this.setBusy();

    var data = new FormData();
    data.append(this.uploadField, item.file);
    if (this.uploadPreset) {
      data.append(this.uploadPresetField, this.uploadPreset);
    }

    fetch(this.endpoint, { method: 'POST', body: data })
      .then(function (res) {
        if (!res.ok) throw new Error('Upload failed');
        return res.json().catch(function () {
          return {};
        });
      })
      .then(function (json) {
        var url = json.secure_url || json.url || (json.data && json.data.url) || '';
        if (!url) throw new Error('No URL returned');
        item.url = url;
        item.status = 'done';
      })
      .catch(function () {
        item.status = 'error';
        self.setError('Some photos failed to upload. You can remove and retry, or submit without them.');
      })
      .then(function () {
        self.render();
        self.setBusy();
      });
  };

  TradeInForm.prototype.isBusy = function () {
    return this.items.some(function (item) {
      return item.status === 'uploading';
    });
  };

  TradeInForm.prototype.setBusy = function () {
    if (!this.submitBtn) return;
    var busy = this.isBusy();
    this.submitBtn.disabled = busy;
    this.submitBtn.classList.toggle('is-busy', busy);
    if (this.statusEl) {
      this.statusEl.hidden = !busy;
      if (busy) this.statusEl.textContent = 'Uploading photos…';
    }
  };

  TradeInForm.prototype.onSubmit = function (e) {
    // Once the async pipeline has run, let the native submit go through untouched.
    if (this.readyToSubmit) return;
    e.preventDefault();
    this.processSubmit();
  };

  TradeInForm.prototype.processSubmit = function () {
    var self = this;
    this.updateFields();
    if (this.refreshOffer) this.refreshOffer();
    if (this.quote && this.quote.priced && !this.selectedValue('contact[Payout choice]')) {
      this.setStatus('Choose store credit or cash for this device.', true);
      var offerIndex = -1;
      this.steps.forEach(function (step, i) {
        if (step.hasAttribute('data-offer-step')) offerIndex = i;
      });
      if (offerIndex >= 0) this.goTo(offerIndex);
      return;
    }
    if (this.commitDraft && this.quote && !this.quote.loading) this.commitDraft();

    if (this.photosRequired && this.items.length === 0) {
      this.setError('Please add at least one photo of your device.');
      if (this.drop) this.drop.focus();
      return;
    }

    if (this.violatesWorkingPolicy()) {
      this.setError('We only accept fully working devices in this category. Gaming consoles are the exception — pick Gaming console if yours is faulty.');
      return;
    }

    if (this.isBusy()) {
      this.setStatus('Please wait — your photos are still uploading.');
      return;
    }

    if (this.submitBtn) this.submitBtn.disabled = true;

    var ensureEncrypted;
    if (this.secureEnabled && !this.encrypted) {
      this.setStatus('Securing your details…');
      ensureEncrypted = this.encryptSensitive().then(function () {
        self.encrypted = true;
      });
    } else {
      ensureEncrypted = Promise.resolve();
    }

    ensureEncrypted
      .then(function () {
        return self.deliverAndSubmit();
      })
      .catch(function () {
        if (self.submitBtn) self.submitBtn.disabled = false;
        self.setStatus(
          'We could not secure your bank/ID details in this browser, so they were not sent. Please try a different browser or contact us.',
          true
        );
      });
  };

  TradeInForm.prototype.deliverAndSubmit = function () {
    var self = this;
    var hasSecret = !!(this.securePayloadField && this.securePayloadField.value);

    if (this.portalEnabled) {
      // Sensitive data goes to the portal ONLY — never include the blob in the Shopify form.
      if (this.securePayloadField) this.securePayloadField.removeAttribute('name');

      return this.postToPortal(true).then(function (ok) {
        if (!ok && hasSecret) {
          // No email fallback for sensitive data — block so nothing is silently lost.
          self.posted = false;
          if (self.submitBtn) self.submitBtn.disabled = false;
          self.setStatus(
            'We couldn’t reach our secure system just now, so nothing was sent. Please try again in a moment.',
            true
          );
          return;
        }
        self.finishSubmit();
      });
    }

    // No portal configured: keep the ENCRYPTED blob in the Shopify submission as a fallback.
    if (hasSecret && this.securePayloadField) {
      this.securePayloadField.setAttribute('name', 'contact[Secure payment & ID]');
    }
    this.finishSubmit();
    return Promise.resolve();
  };

  TradeInForm.prototype.finishSubmit = function () {
    // Remove plaintext bank/ID from the Shopify submission whenever secure mode is on.
    if (this.secureEnabled) this.stripSecureFields();
    this.readyToSubmit = true;
    if (this.submitBtn) this.submitBtn.disabled = false;
    if (this.form.requestSubmit) {
      this.form.requestSubmit();
    } else {
      this.form.submit();
    }
  };

  TradeInForm.prototype.fieldValue = function (name) {
    if (!this.form) return '';
    var radios = this.form.querySelectorAll('input[type=radio][name="contact[' + name + ']"]');
    if (radios.length) {
      var picked = '';
      Array.prototype.forEach.call(radios, function (r) {
        if (r.checked && !r.disabled) picked = r.value;
      });
      return picked;
    }
    var el = this.form.querySelector('[name="contact[' + name + ']"]');
    if (!el || el.disabled) return '';
    return el.value;
  };

  TradeInForm.prototype.postToPortal = function (blocking) {
    if (!this.portalEnabled || this.posted || !this.form) return Promise.resolve(false);
    this.posted = true;

    var included = [];
    var checks = this.form.querySelectorAll('input[type=checkbox][name="contact[Included items][]"]:checked');
    Array.prototype.forEach.call(checks, function (c) { included.push(c.value); });

    var payload = {
      name: this.fieldValue('name'),
      email: this.fieldValue('email'),
      phone: this.fieldValue('phone'),
      category: this.fieldValue('Device category'),
      make: this.fieldValue('Make'),
      model: this.fieldValue('Device model'),
      storage: this.fieldValue('Storage capacity'),
      connectivity: this.fieldValue('Connectivity'),
      caseSize: this.fieldValue('Case size'),
      ram: this.fieldValue('RAM'),
      processor: this.fieldValue('Processor'),
      imei: this.fieldValue('IMEI or serial'),
      condition: this.fieldValue('Condition'),
      screen: this.fieldValue('Screen condition'),
      body: this.fieldValue('Body condition'),
      network: this.fieldValue('Network status'),
      battery: this.fieldValue('Battery health %'),
      included: included,
      notes: this.fieldValue('body'),
      photos: this.fieldValue('Photos attached'),
      photoLinks: this.fieldValue('Photo links'),
      paymentMethod: this.fieldValue('Payment method'),
      idType: this.fieldValue('ID type'),
      secureBlob: this.securePayloadField ? this.securePayloadField.value : ''
    };

    var req;
    try {
      req = fetch(this.portalEndpoint, {
        method: 'POST',
        keepalive: !blocking,
        headers: { 'content-type': 'application/json', 'x-site-token': this.portalToken },
        body: JSON.stringify(payload)
      });
    } catch (err) {
      return Promise.resolve(false);
    }

    if (!blocking) {
      req.catch(function () {});
      return Promise.resolve(true);
    }
    return req
      .then(function (r) {
        return r.ok;
      })
      .catch(function () {
        return false;
      });
  };

  TradeInForm.prototype.setStatus = function (msg, isError) {
    if (!this.statusEl) return;
    this.statusEl.hidden = !msg;
    this.statusEl.textContent = msg || '';
    this.statusEl.classList.toggle('rebx-tradein-form__status--error', !!isError);
  };

  TradeInForm.prototype.stripSecureFields = function () {
    this.secureFields.forEach(function (el) {
      el.disabled = true;
    });
  };

  TradeInForm.prototype.encryptSensitive = function () {
    var self = this;
    var payload = {};
    this.secureFields.forEach(function (el) {
      var key = el.getAttribute('data-secure') || el.name;
      var val = (el.value || '').trim();
      if (val) payload[key] = val;
    });

    // Nothing sensitive entered (e.g. store credit) — clear and move on.
    if (Object.keys(payload).length === 0) {
      if (this.securePayloadField) this.securePayloadField.value = '';
      return Promise.resolve();
    }

    var subtle = window.crypto.subtle;
    var data = new TextEncoder().encode(JSON.stringify(payload));
    var iv = window.crypto.getRandomValues(new Uint8Array(12));
    var aesKey;
    var ciphertext;

    return subtle
      .generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt'])
      .then(function (key) {
        aesKey = key;
        return subtle.encrypt({ name: 'AES-GCM', iv: iv }, aesKey, data);
      })
      .then(function (ct) {
        ciphertext = ct;
        return subtle.exportKey('raw', aesKey);
      })
      .then(function (rawAes) {
        return importPublicKey(self.publicKeyB64).then(function (pubKey) {
          return subtle.encrypt({ name: 'RSA-OAEP' }, pubKey, rawAes);
        });
      })
      .then(function (encKey) {
        var out = {
          v: 1,
          alg: 'RSA-OAEP-256+AES-GCM-256',
          k: bufToB64(encKey),
          iv: bufToB64(iv.buffer),
          d: bufToB64(ciphertext)
        };
        self.securePayloadField.value = JSON.stringify(out);
      });
  };

  function importPublicKey(b64) {
    var der = b64ToBuf(b64);
    return window.crypto.subtle.importKey(
      'spki',
      der,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt']
    );
  }

  function bufToB64(buf) {
    var bytes = new Uint8Array(buf);
    var bin = '';
    for (var i = 0; i < bytes.length; i++) {
      bin += String.fromCharCode(bytes[i]);
    }
    return window.btoa(bin);
  }

  function b64ToBuf(b64) {
    var bin = window.atob(b64);
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) {
      bytes[i] = bin.charCodeAt(i);
    }
    return bytes.buffer;
  }

  var GRADE_TESTS = [
    ['very good', 'Very Good'],
    ['excellent', 'Excellent'],
    ['open box', 'Open Box'],
    ['fair', 'Fair'],
    ['good', 'Good'],
    ['faulty', 'Faulty'],
    ['new', 'New']
  ];

  var COLLECTION_KIND = {
    smartphones: 'phone',
    'smart-watch': 'watch',
    consoles: 'console',
    'headphones-speakers': 'audio',
    ipad: 'tablet',
    tablets: 'tablet',
    laptops: 'laptop'
  };

  function formatPounds(amount) {
    var n = Math.round(Number(amount) || 0);
    return '£' + n.toLocaleString('en-GB');
  }

  function normalizeTitle(title) {
    return String(title || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function boundedContains(hay, needle) {
    var t = String(hay || '').toLowerCase();
    var m = String(needle || '').toLowerCase();
    if (!m) return false;
    var i = t.indexOf(m);
    if (i === -1) return false;
    var before = i === 0 ? '' : t.charAt(i - 1);
    var after = t.charAt(i + m.length);
    if (before && /[a-z0-9]/.test(before)) return false;
    if (after && /[a-z0-9]/.test(after)) return false;
    return true;
  }

  function gradeFromTags(tags) {
    var list = (tags || []).map(function (tag) { return String(tag).toLowerCase(); });
    for (var i = 0; i < GRADE_TESTS.length; i++) {
      var needle = GRADE_TESTS[i][0];
      var re = new RegExp('(^|[^a-z])' + needle + '([^a-z]|$)');
      for (var j = 0; j < list.length; j++) {
        if (re.test(list[j])) return GRADE_TESTS[i][1];
      }
    }
    return '';
  }

  function gradeFromCondition(value) {
    var v = String(value || '').toLowerCase();
    if (v.indexOf('very good') === 0) return 'Very Good';
    if (v.indexOf('excellent') === 0) return 'Excellent';
    if (v.indexOf('good') === 0) return 'Good';
    if (v.indexOf('fair') === 0) return 'Fair';
    if (v.indexOf('faulty') === 0) return 'Faulty';
    return '';
  }

  function kindForProduct(handle, product) {
    if (COLLECTION_KIND[handle]) return COLLECTION_KIND[handle];
    return inferKind((product.product_type || '') + ' ' + (product.title || ''));
  }

  function buildBuyModels() {
    var kinds = ['phone', 'watch', 'console'];
    var models = [];
    kinds.forEach(function (kind) {
      var profile = DEFAULT_PROFILES[kind];
      if (!profile || !profile.makes) return;
      Object.keys(profile.makes).forEach(function (make) {
        profile.makes[make].forEach(function (name) {
          models.push({
            kind: kind,
            vendor: make,
            title: name,
            modelKey: normalizeTitle(name)
          });
        });
      });
    });
    return models;
  }

  function titleMatchesModel(title, model, names) {
    if (!boundedContains(title, model)) return false;
    return !(names || []).some(function (other) {
      return other.length > model.length && boundedContains(other, model) && boundedContains(title, other);
    });
  }

  function isDeviceNotAccessory(title, tags) {
    var text = String(title || '').toLowerCase();
    var tagText = Array.isArray(tags) ? tags.join(' ').toLowerCase() : String(tags || '').toLowerCase();
    if (/\b(case|cover|protector|charger|cable|folio|tempered)\b/.test(text)) return false;
    if (tagText.indexOf('bargain') !== -1 && tagText.indexOf('refurbished') === -1) return false;
    return true;
  }

  TradeInForm.prototype.initPricing = function () {
    var self = this;
    this.searchRoot = document.querySelector('[data-tradein-search]');
    this.searchInput = document.querySelector('[data-tradein-search-input]');
    this.searchResults = document.querySelector('[data-search-results]');
    this.searchEmpty = document.querySelector('[data-search-empty]');
    this.searchStatus = document.querySelector('[data-search-status]');
    this.basketField = this.root.querySelector('[data-basket-field]');
    this.offerRoot = this.root.querySelector('[data-offer-root]');

    var addBtn = this.root.querySelector('[data-offer-add]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        self.addAnother();
      });
    }

    if (this.searchInput) {
      this.searchInput.addEventListener('input', function () {
        if (self.searchTimer) window.clearTimeout(self.searchTimer);
        self.searchTimer = window.setTimeout(function () {
          self.renderSearch(self.searchInput.value);
        }, 120);
      });
    }

    if (this.searchResults) {
      this.searchResults.addEventListener('click', function (e) {
        var hit = e.target.closest('[data-group-index]');
        if (!hit) return;
        var group = (self.visibleHits || [])[parseInt(hit.getAttribute('data-group-index'), 10)];
        if (group) self.openFromResult(group);
      });
    }

    this.buyModels = buildBuyModels();
    this.modelNames = this.buyModels.map(function (model) { return model.title; });
    this.suggestCache = {};
    this.suggestPending = {};
    this.stockItems = [];
    this.visibleHits = [];
    this.loadCatalog();
  };

  TradeInForm.prototype.loadCatalog = function () {
    var self = this;
    var handles = this.catalogHandles.length
      ? this.catalogHandles
      : ['smartphones', 'smart-watch', 'consoles'];
    this.catalogLoading = true;

    loadTradePrices(this.root.getAttribute('data-price-list')).then(function () {
      if (self.searchInput) self.renderSearch(self.searchInput.value);
      if (self.steps[self.current] && self.steps[self.current].hasAttribute('data-offer-step')) {
        self.refreshOffer();
      }
    });

    Promise.all(handles.map(function (handle) {
      return fetchCollection(handle);
    })).then(function (lists) {
      var products = [];
      lists.forEach(function (list) {
        products = products.concat(list);
      });
      self.indexCatalog(products);
      self.catalogLoading = false;
      if (self.searchStatus) self.searchStatus.hidden = true;
      self.renderSearch(self.searchInput ? self.searchInput.value : '');
      if (self.steps[self.current] && self.steps[self.current].hasAttribute('data-offer-step')) {
        self.refreshOffer();
      }
    }).catch(function () {
      self.catalogLoading = false;
      self.groupList = [];
      self.groupMap = {};
      self.modelQty = {};
      if (self.searchStatus) {
        self.searchStatus.hidden = false;
        self.searchStatus.textContent = 'Prices could not load. You can still send the device for a manual valuation.';
      }
    });
  };

  function fetchCollection(handle) {
    var page = 1;
    var all = [];

    function next() {
      return fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + page)
        .then(function (res) {
          if (!res.ok) throw new Error('catalog');
          return res.json();
        })
        .then(function (data) {
          var products = (data && data.products) || [];
          products.forEach(function (product) {
            product.__collection = handle;
          });
          all = all.concat(products);
          if (products.length === 250 && page < 6) {
            page += 1;
            return next();
          }
          return all;
        });
    }

    return next();
  }

  TradeInForm.prototype.indexCatalog = function (products) {
    var groups = {};
    var modelQty = {};
    var stockItems = [];
    products.forEach(function (product) {
      var price = null;
      var qty = 0;
      (product.variants || []).forEach(function (variant) {
        if (!variant.available) return;
        var amount = parseFloat(variant.price);
        if (!isNaN(amount) && (price == null || amount < price)) price = amount;
        qty += 1;
      });
      if (!qty || !(price > 0)) return;
      var grade = gradeFromTags(product.tags) || '';
      var image = (product.images && product.images[0] && product.images[0].src) || '';
      stockItems.push({
        title: product.title,
        grade: grade,
        price: price,
        qty: qty,
        image: image,
        vendor: product.vendor || '',
        kind: kindForProduct(product.__collection, product)
      });
      if (!grade) return;
      var modelKey = normalizeTitle(product.title);
      var id = modelKey + '|' + grade;
      if (!groups[id]) {
        groups[id] = {
          id: id,
          modelKey: modelKey,
          grade: grade,
          title: product.title,
          price: price,
          qty: qty,
          image: image,
          vendor: product.vendor || '',
          kind: kindForProduct(product.__collection, product)
        };
      } else {
        groups[id].qty += qty;
        if (price < groups[id].price) groups[id].price = price;
      }
      modelQty[modelKey] = (modelQty[modelKey] || 0) + qty;
    });
    this.stockItems = stockItems;
    this.groupMap = groups;
    this.modelQty = modelQty;
    this.groupList = Object.keys(groups).map(function (id) { return groups[id]; });
    this.groupList.sort(function (a, b) {
      return a.title.localeCompare(b.title) || a.grade.localeCompare(b.grade);
    });
  };

  TradeInForm.prototype.quoteFor = function (group) {
    if (!group) return null;
    var listed = matchTradePrice(group.title);
    if (listed) {
      return {
        priced: true,
        loading: false,
        title: group.title,
        grade: group.grade,
        credit: Math.round(listed.cash * 60 / 55),
        cash: listed.cash,
        highStock: false
      };
    }
    if (!(group.price > 0)) return null;
    var stock = typeof group.qty === 'number' ? group.qty : (this.modelQty[group.modelKey] || 0);
    var plenty = stock >= this.stockThreshold;
    var creditRate = plenty ? this.creditRateStock : this.creditRate;
    var cashRate = plenty ? this.cashRateStock : this.cashRate;
    return {
      priced: true,
      loading: false,
      title: group.title,
      grade: group.grade,
      credit: Math.round(group.price * creditRate),
      cash: Math.round(group.price * cashRate),
      highStock: plenty
    };
  };

  TradeInForm.prototype.suggestItems = function () {
    var items = [];
    var cache = this.suggestCache || {};
    Object.keys(cache).forEach(function (key) {
      items = items.concat(cache[key] || []);
    });
    return items;
  };

  TradeInForm.prototype.priceForModel = function (name, grade) {
    var wanted = String(name || '').trim();
    if (wanted.length < 2) return null;
    var names = this.modelNames || [];
    var stockHits = (this.stockItems || []).filter(function (item) {
      return item.price > 0 && titleMatchesModel(item.title, wanted, names);
    });
    var soldHits = this.suggestItems().filter(function (item) {
      return item.price > 0 && titleMatchesModel(item.title, wanted, names);
    });
    var hits = stockHits.concat(soldHits);
    if (!hits.length) return null;
    var graded = grade
      ? hits.filter(function (item) { return item.grade === grade; })
      : [];
    var pool = graded.length ? graded : (stockHits.length ? stockHits : hits);
    var price = pool[0].price;
    pool.forEach(function (item) {
      if (item.price < price) price = item.price;
    });
    var qty = 0;
    stockHits.forEach(function (item) { qty += item.qty; });
    var buy = null;
    (this.buyModels || []).forEach(function (model) {
      if (model.modelKey === normalizeTitle(wanted)) buy = model;
    });
    var image = '';
    var kind = buy ? buy.kind : '';
    var vendor = buy ? buy.vendor : '';
    pool.forEach(function (item) {
      if (!image && item.image) image = item.image;
      if (!kind && item.kind) kind = item.kind;
      if (!vendor && item.vendor) vendor = item.vendor;
    });
    return {
      title: buy ? buy.title : wanted,
      modelKey: normalizeTitle(buy ? buy.title : wanted),
      grade: grade || '',
      price: price,
      qty: qty,
      image: image,
      kind: kind,
      vendor: vendor
    };
  };

  TradeInForm.prototype.loadSuggest = function (query) {
    var self = this;
    var key = normalizeTitle(query);
    if (!key || this.suggestCache[key] || this.suggestPending[key]) return;
    this.suggestPending[key] = true;
    var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=show';
    fetch(url)
      .then(function (res) {
        return res.ok ? res.json() : {};
      })
      .then(function (data) {
        var products = (((data || {}).resources || {}).results || {}).products || [];
        self.suggestCache[key] = products.map(function (product) {
          if (!isDeviceNotAccessory(product.title, product.tags)) return null;
          var amount = parseFloat(product.price);
          if (!(amount > 0)) return null;
          var image = '';
          if (product.featured_image && product.featured_image.url) image = product.featured_image.url;
          else if (typeof product.image === 'string') image = product.image;
          else if (product.image && product.image.src) image = product.image.src;
          return {
            title: product.title,
            price: amount,
            qty: product.available ? 1 : 0,
            grade: gradeFromTags(product.tags),
            image: image,
            vendor: product.vendor || '',
            kind: inferKind((product.type || '') + ' ' + product.title)
          };
        }).filter(Boolean);
      })
      .catch(function () {
        self.suggestCache[key] = [];
      })
      .then(function () {
        self.suggestPending[key] = false;
        if (self.searchInput && normalizeTitle(self.searchInput.value)) {
          self.renderSearch(self.searchInput.value);
        }
        if (self.steps[self.current] && self.steps[self.current].hasAttribute('data-offer-step')) {
          self.refreshOffer();
        }
      });
  };

  TradeInForm.prototype.renderSearch = function (query) {
    if (!this.searchResults) return;
    var q = normalizeTitle(query);
    var tokens = q.split(' ').filter(Boolean);
    if (this.catalogLoading) return;
    if (tokens.length < 2 && q.length < 3) {
      this.searchResults.hidden = true;
      this.searchResults.innerHTML = '';
      this.visibleHits = [];
      if (this.searchEmpty) this.searchEmpty.hidden = true;
      return;
    }
    var self = this;
    var modelHits = (this.buyModels || []).filter(function (model) {
      return tokens.every(function (token) { return model.modelKey.indexOf(token) !== -1; });
    }).slice(0, 12);
    var hits = [];
    if (modelHits.length) {
      modelHits.forEach(function (model) {
        var priced = self.priceForModel(model.title);
        hits.push(priced || {
          title: model.title,
          modelKey: model.modelKey,
          grade: '',
          price: 0,
          qty: 0,
          image: '',
          kind: model.kind,
          vendor: model.vendor
        });
        if (!priced && !self.suggestCache[model.modelKey]) self.loadSuggest(model.title);
      });
    } else {
      (this.groupList || []).forEach(function (group) {
        var ok = tokens.every(function (token) { return group.modelKey.indexOf(token) !== -1; });
        if (ok) hits.push(group);
      });
      hits = hits.slice(0, 12);
    }
    this.visibleHits = hits;
    if (!hits.length) {
      this.searchResults.hidden = true;
      this.searchResults.innerHTML = '';
      if (this.searchEmpty) this.searchEmpty.hidden = false;
      return;
    }
    if (this.searchEmpty) this.searchEmpty.hidden = true;
    this.searchResults.innerHTML = hits.map(function (group, index) {
      var quote = self.quoteFor(group);
      var img = group.image
        ? '<img class="rebx-tradein-hit__img" alt="" src="' + escapeHtml(group.image) + '" width="72" height="72">'
        : '';
      var grade = group.grade
        ? '<span class="rebx-tradein-hit__grade">' + escapeHtml(group.grade) + '</span>'
        : '';
      var prices = quote
        ? '<span><strong>' + formatPounds(quote.credit) + '</strong> store credit</span>' +
          '<span><strong>' + formatPounds(quote.cash) + '</strong> cash</span>'
        : '<span>We buy this. We’ll confirm the price.</span>';
      return (
        '<button type="button" class="rebx-tradein-hit" data-group-index="' + index + '">' +
          img +
          '<span class="rebx-tradein-hit__main">' +
            grade +
            '<span class="rebx-tradein-hit__title">' + escapeHtml(group.title) + '</span>' +
          '</span>' +
          '<span class="rebx-tradein-hit__prices">' + prices + '</span>' +
        '</button>'
      );
    }).join('');
    this.searchResults.hidden = false;
  };

  TradeInForm.prototype.openFromListing = function (product, stockQty) {
    var title = product.title || '';
    var tags = product.tags;
    if (typeof tags === 'string') {
      tags = tags.split(',').map(function (tag) { return tag.trim(); }).filter(Boolean);
    }
    var kind = inferKind((product.type || '') + ' ' + title);
    if (kind !== 'phone' && kind !== 'watch' && kind !== 'console') return;
    var labels = {
      phone: 'smartphone',
      watch: 'smart watch',
      console: 'console',
      audio: 'headphones',
      laptop: 'laptop',
      tablet: 'tablet',
      other: 'device'
    };
    var profileKind = kind === 'tablet' ? 'phone' : (kind || 'other');
    var hasTile = false;
    this.categoryRadios.forEach(function (radio) {
      if ((radio.getAttribute('data-device-kind') || '').trim().toLowerCase() === kind) hasTile = true;
    });
    if (hasTile) {
      this.openModal(kind, labels[kind] || '');
    } else {
      this.openModal('', '');
      this.applyDevice(profileKind);
      this.updateModalTitle(labels[kind] || 'device');
      this.categoryLocked = true;
    }
    var cents = typeof product.price === 'number' ? product.price : Math.round(parseFloat(product.price) * 100);
    var grade = gradeFromTags(tags);
    var qty = stockQty > 0 ? stockQty : 1;
    this.priceAnchor = {
      modelKey: normalizeTitle(title),
      grade: grade,
      title: title,
      price: cents / 100,
      qty: qty,
      exact: true
    };
    this.draftId = null;
    this.prefillFromGroup({
      title: title,
      grade: grade,
      vendor: product.vendor || '',
      kind: hasTile ? kind : profileKind
    });
    var storageMatch = String(title).match(/\b(\d+)\s?(GB|TB)\b/i);
    if (storageMatch) {
      this.checkRadioValue('contact[Storage capacity]', (storageMatch[1] + storageMatch[2]).toUpperCase());
    }
    this.refreshFlow();
    var conditionIndex = 1;
    this.steps.forEach(function (step, i) {
      var name = (step.getAttribute('data-step-title') || '').toLowerCase();
      if (name.indexOf('condition') !== -1) conditionIndex = i;
    });
    this.goTo(conditionIndex, true);
  };

  TradeInForm.prototype.openFromResult = function (group) {
    var labels = {
      phone: 'smartphone',
      watch: 'smart watch',
      console: 'console',
      audio: 'headphones',
      laptop: 'laptop',
      tablet: 'tablet'
    };
    this.openModal(group.kind || '', labels[group.kind] || '');
    this.priceAnchor = {
      modelKey: group.modelKey,
      grade: group.grade,
      title: group.title,
      price: group.price
    };
    this.draftId = null;
    this.prefillFromGroup(group);
  };

  TradeInForm.prototype.checkRadioValue = function (name, value) {
    var radios = this.root.querySelectorAll('input[type=radio][name="' + name + '"]');
    var found = null;
    Array.prototype.forEach.call(radios, function (radio) {
      if (radio.value === value) found = radio;
    });
    if (found) found.checked = true;
    return found;
  };

  TradeInForm.prototype.useFreeModel = function (title) {
    this.checkRadioValue('rebx_model_choice', '__other__');
    if (this.modelTextInput) this.modelTextInput.value = title;
    this.onModelChange();
  };

  TradeInForm.prototype.prefillFromGroup = function (group) {
    var profile = this.activeProfile || {};
    var makes = profile.makes || {};
    var make = '';
    var vendor = String(group.vendor || '').toLowerCase();
    Object.keys(makes).forEach(function (key) {
      if (key.toLowerCase() === vendor) make = key;
    });
    if (!make) {
      Object.keys(makes).forEach(function (key) {
        if (boundedContains(group.title, key) && key.length > make.length) make = key;
      });
    }
    if (make) {
      this.checkRadioValue('contact[Make]', make);
      this.onMakeChange();
      var best = '';
      (makes[make] || []).forEach(function (model) {
        if (boundedContains(group.title, model) && model.length > best.length) best = model;
      });
      if (best) {
        this.checkRadioValue('rebx_model_choice', best);
        this.onModelChange();
      } else {
        this.useFreeModel(group.title);
      }
    } else if (Object.keys(makes).length) {
      this.checkRadioValue('contact[Make]', 'Other brand');
      this.onMakeChange();
      this.useFreeModel(group.title);
    } else if (this.modelHidden) {
      this.modelHidden.value = group.title;
      if (this.modelTextInput) this.modelTextInput.value = group.title;
    }
    this.selectGrade(group.grade);
    this.forceActive = null;
    this.refreshFlow();
  };

  TradeInForm.prototype.selectGrade = function (grade) {
    var want = String(grade || '').toLowerCase();
    if (!want || want === 'open box' || want === 'new') return;
    var radios = this.root.querySelectorAll('input[name="contact[Condition]"]');
    Array.prototype.forEach.call(radios, function (radio) {
      var value = radio.value.toLowerCase();
      var ok = false;
      if (want === 'very good') ok = value.indexOf('very good') === 0;
      else if (want === 'good') ok = value.indexOf('good') === 0;
      else ok = value.indexOf(want) === 0;
      if (ok) radio.checked = true;
    });
  };

  TradeInForm.prototype.anchorMatches = function () {
    if (!this.priceAnchor) return false;
    var model = this.modelHidden && this.modelHidden.value ? this.modelHidden.value.trim().toLowerCase() : '';
    if (!model) return true;
    var title = this.priceAnchor.title.toLowerCase();
    if (model === title || model === this.priceAnchor.modelKey) return true;
    if (!boundedContains(title, model)) return false;
    var makes = (this.activeProfile && this.activeProfile.makes) || {};
    var longer = false;
    Object.keys(makes).forEach(function (key) {
      (makes[key] || []).forEach(function (name) {
        if (name.length > model.length && boundedContains(title, name)) longer = true;
      });
    });
    return !longer;
  };

  TradeInForm.prototype.matchGroup = function (text, grade) {
    var q = normalizeTitle(text);
    if (q.length < 2) return null;
    var hits = [];
    var keys = {};
    this.groupList.forEach(function (group) {
      if (group.grade !== grade) return;
      if (group.modelKey === q || boundedContains(group.title, q) || boundedContains(q, group.modelKey)) {
        if (!keys[group.modelKey]) {
          keys[group.modelKey] = true;
          hits.push(group);
        }
      }
    });
    if (hits.length === 1) return hits[0];
    return null;
  };

  TradeInForm.prototype.refreshOffer = function () {
    if (!this.offerRoot) return;
    var loadingEl = this.offerRoot.querySelector('[data-offer-loading]');
    var pricedEl = this.offerRoot.querySelector('[data-offer-priced]');
    var manualEl = this.offerRoot.querySelector('[data-offer-manual]');
    var forEl = this.offerRoot.querySelector('[data-offer-for]');
    var creditEl = this.offerRoot.querySelector('[data-offer-credit]');
    var cashEl = this.offerRoot.querySelector('[data-offer-cash]');
    var radios = this.offerRoot.querySelectorAll('[data-payout-input]');

    if (this.catalogLoading) {
      this.quote = { priced: false, loading: true, title: '', grade: '' };
      if (loadingEl) loadingEl.hidden = false;
      if (pricedEl) pricedEl.hidden = true;
      if (manualEl) manualEl.hidden = true;
      Array.prototype.forEach.call(radios, function (radio) {
        radio.disabled = true;
        radio.required = false;
      });
      return;
    }

    var conditionGrade = gradeFromCondition(this.fieldValue('Condition'));
    var modelName = (this.modelHidden && this.modelHidden.value) || (this.priceAnchor && this.priceAnchor.title) || '';
    var anchor = this.priceAnchor;
    var group = null;
    if (anchor && anchor.exact && anchor.price > 0 && conditionGrade && conditionGrade === anchor.grade) {
      group = anchor;
    } else {
      group = this.priceForModel(modelName, conditionGrade);
      if (!group && anchor && anchor.price > 0 && this.anchorMatches()) {
        group = anchor;
      }
    }
    if (!group && modelName && !this.suggestCache[normalizeTitle(modelName)]) {
      this.loadSuggest(modelName);
    }

    var quote = this.quoteFor(group);
    if (anchor && anchor.exact && this.anchorMatches()) {
      var listed = matchTradePrice(anchor.title);
      if (listed) {
        quote = {
          priced: true,
          loading: false,
          title: anchor.title,
          grade: conditionGrade || anchor.grade || '',
          credit: Math.round(listed.cash * 60 / 55),
          cash: listed.cash,
          highStock: false
        };
      }
    }
    if (!quote) {
      var title = (this.priceAnchor && this.anchorMatches() && this.priceAnchor.title) || this.fieldValue('Device model') || '';
      quote = {
        priced: false,
        loading: false,
        title: title,
        grade: conditionGrade || (this.priceAnchor && this.priceAnchor.grade) || ''
      };
    }
    this.quote = quote;

    if (loadingEl) loadingEl.hidden = true;
    if (pricedEl) pricedEl.hidden = !quote.priced;
    if (manualEl) manualEl.hidden = !!quote.priced;
    Array.prototype.forEach.call(radios, function (radio) {
      radio.disabled = !quote.priced;
      radio.required = !!quote.priced;
      if (!quote.priced) radio.checked = false;
    });
    if (quote.priced) {
      if (forEl) forEl.textContent = quote.grade ? quote.grade + ' · ' + quote.title : quote.title;
      if (creditEl) creditEl.textContent = formatPounds(quote.credit);
      if (cashEl) cashEl.textContent = formatPounds(quote.cash);
    }
  };

  TradeInForm.prototype.syncPayoutToPayment = function () {
    var choice = this.selectedValue('contact[Payout choice]');
    if (!choice || !this.payRadios) return;
    this.payRadios.forEach(function (radio) {
      if (choice === 'Store credit' && /store credit/i.test(radio.value)) radio.checked = true;
      if (choice === 'Cash' && (radio.getAttribute('data-pay-target') || '') === 'bank') radio.checked = true;
    });
    if (this.updatePayment) this.updatePayment();
  };

  TradeInForm.prototype.buildDraft = function () {
    if (!this.quote || this.quote.loading) return null;
    var title = String(this.quote.title || '').trim();
    if (!title) return null;
    var payout = this.quote.priced ? this.selectedValue('contact[Payout choice]') : 'Manual';
    if (this.quote.priced && !payout) return null;
    return {
      id: this.draftId,
      title: title,
      grade: this.quote.grade || '',
      payout: payout || 'Manual',
      credit: this.quote.priced ? this.quote.credit : null,
      cash: this.quote.priced ? this.quote.cash : null
    };
  };

  TradeInForm.prototype.commitDraft = function () {
    var draft = this.buildDraft();
    if (!draft) return null;
    if (!this.draftId) {
      this.basketUid += 1;
      this.draftId = this.basketUid;
    }
    draft.id = this.draftId;
    var replaced = false;
    this.basket = this.basket.map(function (item) {
      if (item.id === draft.id) {
        replaced = true;
        return draft;
      }
      return item;
    });
    if (!replaced) this.basket.push(draft);
    this.renderBasket();
    return draft;
  };

  TradeInForm.prototype.addAnother = function () {
    if (this.steps[this.current] && this.steps[this.current].hasAttribute('data-offer-step')) {
      if (!this.validateStep(this.current)) return;
    }
    this.commitDraft();
    this.draftId = null;
    this.priceAnchor = null;
    this.closeModal();
    var search = document.querySelector('[data-tradein-search]');
    if (search && search.scrollIntoView) search.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (this.searchInput) {
      try { this.searchInput.focus({ preventScroll: true }); } catch (err) { this.searchInput.focus(); }
    }
  };

  TradeInForm.prototype.removeBasketItem = function (id) {
    this.basket = this.basket.filter(function (item) { return item.id !== id; });
    if (this.draftId === id) this.draftId = null;
    this.renderBasket();
  };

  TradeInForm.prototype.basketTotals = function () {
    var credit = 0;
    var cash = 0;
    var manual = 0;
    this.basket.forEach(function (item) {
      if (item.credit == null || item.cash == null) manual += 1;
      else {
        credit += item.credit;
        cash += item.cash;
      }
    });
    return { credit: credit, cash: cash, manual: manual };
  };

  TradeInForm.prototype.basketText = function () {
    var self = this;
    if (!this.basket.length) return '';
    var lines = this.basket.map(function (item, index) {
      var price = item.credit == null
        ? 'Price confirmed after inspection'
        : 'store credit ' + formatPounds(item.credit) + ', cash ' + formatPounds(item.cash) + ', chose ' + item.payout;
      return (index + 1) + '. ' + item.title + (item.grade ? ' | ' + item.grade : '') + ' | ' + price;
    });
    var totals = this.basketTotals();
    lines.push('Store credit total: ' + formatPounds(totals.credit));
    lines.push('Cash total: ' + formatPounds(totals.cash));
    if (totals.manual) {
      lines.push(totals.manual + (totals.manual === 1 ? ' item' : ' items') + ' to be valued manually');
    }
    return lines.join('\n');
  };

  TradeInForm.prototype.renderBasket = function () {
    var self = this;
    if (this.basketField) this.basketField.value = this.basketText();
    var mounts = document.querySelectorAll('[data-basket-mount]');
    if (!this.basket.length) {
      Array.prototype.forEach.call(mounts, function (mount) {
        mount.hidden = true;
        mount.innerHTML = '';
      });
      return;
    }
    var totals = this.basketTotals();
    var rows = this.basket.map(function (item) {
      var price = item.credit == null
        ? '<span class="rebx-tradein-basket__manual">We’ll confirm the price</span>'
        : '<span class="rebx-tradein-basket__money">' + formatPounds(item.credit) + ' credit</span>' +
          '<span class="rebx-tradein-basket__money">' + formatPounds(item.cash) + ' cash</span>' +
          '<span class="rebx-tradein-basket__chose">Chose ' + escapeHtml(item.payout) + '</span>';
      return (
        '<li class="rebx-tradein-basket__item">' +
          '<span class="rebx-tradein-basket__name">' + escapeHtml(item.title) +
            (item.grade ? ' <span class="rebx-tradein-basket__grade">' + escapeHtml(item.grade) + '</span>' : '') +
          '</span>' +
          '<span class="rebx-tradein-basket__prices">' + price + '</span>' +
          '<button type="button" class="rebx-tradein-basket__remove" data-basket-remove="' + item.id + '">Remove</button>' +
        '</li>'
      );
    }).join('');
    var html =
      '<h3 class="rebx-tradein-basket__title">Selling items</h3>' +
      '<ul class="rebx-tradein-basket__list">' + rows + '</ul>' +
      '<p class="rebx-tradein-basket__totals">' +
        '<span>Store credit total <strong>' + formatPounds(totals.credit) + '</strong></span>' +
        '<span>Cash total <strong>' + formatPounds(totals.cash) + '</strong></span>' +
      '</p>' +
      (totals.manual
        ? '<p class="rebx-tradein-basket__pending">' + totals.manual +
          (totals.manual === 1 ? ' item has' : ' items have') +
          ' no automatic price. We’ll value ' + (totals.manual === 1 ? 'it' : 'them') + ' from the details you send.</p>'
        : '');
    Array.prototype.forEach.call(mounts, function (mount) {
      mount.hidden = false;
      mount.innerHTML = html;
      Array.prototype.forEach.call(mount.querySelectorAll('[data-basket-remove]'), function (btn) {
        btn.addEventListener('click', function () {
          self.removeBasketItem(parseInt(btn.getAttribute('data-basket-remove'), 10));
        });
      });
    });
  };

  function initTradeInFunnel() {
    if (!document.querySelector('.template-page-trade-in')) return;
    if (document.__rebxTradeInFunnel) return;

    var categories = document.querySelector('[data-tradein-categories]');
    var prompt = document.querySelector('[data-tradein-prompt]');
    var sticky = null;
    var funnelStarted = false;

    function scrollToCategories(highlight) {
      if (!categories) return;
      var top = categories.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({ top: top, behavior: 'smooth' });
      if (highlight) {
        categories.classList.add('rhd-categories--highlight');
        window.setTimeout(function () {
          categories.classList.remove('rhd-categories--highlight');
        }, 2000);
      }
    }

    function markStep(step) {
      document.querySelectorAll('[data-funnel-step]').forEach(function (el) {
        var n = parseInt(el.getAttribute('data-funnel-step'), 10);
        el.classList.toggle('rhd-tradein-funnel__step--current', n === step);
        el.classList.toggle('rhd-tradein-funnel__step--done', n < step);
      });
    }

    function hideSticky() {
      if (sticky) sticky.classList.remove('is-visible');
    }

    function showStickyIfNeeded() {
      if (!sticky || funnelStarted) return;
      if (document.documentElement.classList.contains('rebx-tradein-modal-open')) return;
      sticky.classList.add('is-visible');
    }

    document.__rebxTradeInFunnel = {
      onFunnelStart: function () {
        if (funnelStarted) return;
        funnelStarted = true;
        markStep(2);
        if (prompt) prompt.hidden = true;
        hideSticky();
      },
      onFunnelReset: function () {
        funnelStarted = false;
        markStep(1);
        if (prompt) prompt.hidden = false;
      },
      scrollToCategories: scrollToCategories
    };

    if (!document.__rebxTradeInFunnelClickBound) {
      document.__rebxTradeInFunnelClickBound = true;
      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-tradein-scroll-categories]')) {
          e.preventDefault();
          scrollToCategories(true);
          return;
        }

        var hashLink = e.target.closest('a[href="#trade-in-form"], a[href="#trade-in"]');
        if (hashLink && !e.target.closest('[data-tradein-open]')) {
          e.preventDefault();
          scrollToCategories(true);
        }
      });
    }

    if (categories && 'IntersectionObserver' in window) {
      sticky = document.createElement('div');
      sticky.className = 'rebx-tradein-sticky';
      sticky.setAttribute('aria-live', 'polite');
      sticky.innerHTML =
        '<div class="rebx-tradein-sticky__inner">' +
        '<p class="rebx-tradein-sticky__copy">Ready to trade in? <span>Pick your device type to start.</span></p>' +
        '<button type="button" class="rebx-tradein-sticky__btn" data-tradein-scroll-categories>Choose device</button>' +
        '</div>';
      document.body.appendChild(sticky);

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              hideSticky();
            } else if (entry.boundingClientRect.top < 0) {
              showStickyIfNeeded();
            }
          });
        },
        { threshold: 0 }
      );
      observer.observe(categories);
    }

    var hash = (window.location.hash || '').toLowerCase();
    if (hash === '#trade-in-form' || hash === '#trade-in' || hash === '#trade-in-categories') {
      window.setTimeout(function () {
        scrollToCategories(hash !== '#trade-in-categories');
      }, 150);
    }
  }

  function placeTradeInSearch() {
    var search = document.querySelector('[data-tradein-search]');
    var container = document.querySelector('[data-tradein-categories] .container');
    if (search && container && search.parentNode !== container) {
      container.insertBefore(search, container.firstChild);
    }
  }

  function consumeListingHandoff() {
    var params = new URLSearchParams(window.location.search);
    var handle = (params.get('item') || '').trim();
    if (!handle) return;
    var stock = parseInt(params.get('stock'), 10);
    params.delete('item');
    params.delete('stock');
    var query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
    var formRoot = document.querySelector('[data-tradein-form]');
    if (!formRoot || !formRoot.__rebxTradeIn) return;
    fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (product) {
        if (!product || !formRoot.__rebxTradeIn) return;
        formRoot.__rebxTradeIn.openFromListing(product, stock);
      })
      .catch(function () {});
  }

  function init() {
    placeTradeInSearch();
    initTradeInFunnel();

    var roots = document.querySelectorAll('[data-tradein-form]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__rebxTradeIn) return;
      root.__rebxTradeIn = new TradeInForm(root);
    });

    consumeListingHandoff();

    if (!document.__rebxTradeInOpenBound) {
      document.__rebxTradeInOpenBound = true;
      document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-tradein-open]');
        if (!trigger) return;
        e.preventDefault();
        var kind = (trigger.getAttribute('data-tradein-kind') || '').trim().toLowerCase();
        var label = (trigger.getAttribute('data-tradein-label') || '').trim();
        var formRoot = document.querySelector('[data-tradein-form]');
        if (formRoot && formRoot.__rebxTradeIn) {
          formRoot.__rebxTradeIn.openModal(kind, label);
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
